<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex flex-col lg:flex-row overflow-hidden">
    <!-- Mobile Header -->
    <div class="lg:hidden bg-gradient-to-r from-slate-900 to-slate-800 text-white p-4 shadow-lg relative overflow-hidden">
      <!-- Decorative elements for mobile -->
      <div class="absolute -top-5 -right-5 w-20 h-20 bg-gradient-radial from-blue-500/20 to-transparent rounded-full blur-xl"></div>
      
      <div class="flex items-center justify-between relative z-10">
        <div class="flex items-center space-x-3">
          <div class="transform hover:scale-105 transition-all duration-300">
            <EcomacLogo 
              variant="white" 
              size="small" 
              orientation="horizontal" 
              :show-text="false"
            />
          </div>
          <div>
            <h1 class="text-lg font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Portal de Soporte</h1>
            <p class="text-slate-300 text-xs hidden">Ecomac Digital</p>
          </div>
        </div>
        
        <!-- Mobile menu button -->
        <button @click="toggleMobileMenu" class="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors">
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
          </svg>
        </button>
      </div>
      
      <!-- Mobile Navigation (expandable) -->
      <div v-if="mobileMenuOpen" class="mt-4 pb-2 animate-slide-down">
        <nav class="grid grid-cols-2 gap-3">
          <button @click="setActivePanel('dashboard'); closeMobileMenu()" class="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300" 
                  :class="activePanel === 'dashboard' 
                    ? 'bg-gradient-to-r from-blue-500/30 to-purple-500/30 border border-white/20' 
                    : 'hover:bg-white/10'"
          >
            <div class="w-8 h-8 rounded-lg flex items-center justify-center" 
                 :class="activePanel === 'dashboard' 
                   ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                   : 'bg-white/10'"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
            </div>
            <span class="font-medium text-sm">Dashboard</span>
          </button>
          
          <button @click="setActivePanel('tickets'); closeMobileMenu()" class="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300"
                  :class="activePanel === 'tickets' 
                    ? 'bg-gradient-to-r from-blue-500/30 to-purple-500/30 border border-white/20' 
                    : 'hover:bg-white/10'"
          >
            <div class="w-8 h-8 rounded-lg flex items-center justify-center" 
                 :class="activePanel === 'tickets' 
                   ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                   : 'bg-white/10'"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
              </svg>
            </div>
            <span class="font-medium text-sm">Tickets</span>
          </button>
          
          <button @click="setActivePanel('chat'); closeMobileMenu()" class="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300"
                  :class="activePanel === 'chat' 
                    ? 'bg-gradient-to-r from-blue-500/30 to-purple-500/30 border border-white/20' 
                    : 'hover:bg-white/10'"
          >
            <div class="w-8 h-8 rounded-lg flex items-center justify-center relative" 
                 :class="activePanel === 'chat' 
                   ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                   : 'bg-white/10'"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <div class="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full border border-slate-900 animate-pulse"></div>
            </div>
            <span class="font-medium text-sm">Elias AI</span>
          </button>
          
          <button @click="setActivePanel('stats'); closeMobileMenu()" class="flex items-center space-x-2 px-3 py-2 rounded-lg transition-all duration-300"
                  :class="activePanel === 'stats' 
                    ? 'bg-gradient-to-r from-blue-500/30 to-purple-500/30 border border-white/20' 
                    : 'hover:bg-white/10'"
          >
            <div class="w-8 h-8 rounded-lg flex items-center justify-center" 
                 :class="activePanel === 'stats' 
                   ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                   : 'bg-white/10'"
            >
              <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <span class="font-medium text-sm">Estadísticas</span>
          </button>
        </nav>
        
        <!-- Mobile user section -->
        <div class="mt-4 pt-4 border-t border-white/10">
          <div class="bg-white/5 rounded-lg p-3 mb-3 backdrop-blur-sm border border-white/10">
            <div class="flex items-center space-x-3">
              <div class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-bold text-xs">
                {{ authStore.user?.email ? authStore.user.email.charAt(0).toUpperCase() : 'U' }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-white font-medium text-sm truncate">{{ authStore.user?.name || authStore.user?.email || 'Usuario' }}</p>
                <p class="text-slate-300 text-xs truncate">{{ authStore.user?.email || 'user@ecomac.com' }}</p>
              </div>
            </div>
          </div>
          
          <button @click="logout" class="w-full flex items-center justify-center space-x-2 px-3 py-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-400/30 transition-all duration-300">
            <svg class="h-4 w-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
            <span class="font-medium text-red-400 text-sm">Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </div>
    
    <!-- Desktop Sidebar with Glass Effect -->
    <aside class="hidden lg:flex w-72 xl:w-80 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden shadow-lg">
      <!-- Glassmorphism overlay -->
      <div class="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-black/5 backdrop-blur-sm"></div>
      
      <!-- Decorative elements -->
      <div class="absolute -top-10 -right-10 w-40 h-40 bg-gradient-radial from-blue-500/20 to-transparent rounded-full blur-xl"></div>
      <div class="absolute -bottom-10 -left-10 w-32 h-32 bg-gradient-radial from-purple-500/20 to-transparent rounded-full blur-xl"></div>
      
      <div class="relative z-10 p-8 flex flex-col h-full">
        <!-- Logo Section -->
        <div class="mb-12 text-center">
          <div class="mx-auto mb-4 transform hover:scale-105 transition-all duration-300">
            <EcomacLogo 
              variant="white" 
              size="medium" 
              orientation="vertical" 
              :show-text="false"
            />
          </div>
          <h1 class="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">Portal de Soporte</h1>
          <p class="text-slate-300 text-sm mt-1 hidden lg:block">Ecomac Digital</p>
        </div>
        
        <!-- Navigation -->
        <nav class="flex-1 space-y-3">
          <div class="space-y-2">
            <button @click="setActivePanel('dashboard')" class="group w-full flex items-center space-x-4 px-4 py-3.5 rounded-xl transition-all duration-300 hover:translate-x-1" 
                    :class="activePanel === 'dashboard' 
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 shadow-lg border border-white/10 backdrop-blur-sm' 
                      : 'hover:bg-white/10 hover:shadow-md'">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300" 
                   :class="activePanel === 'dashboard' 
                     ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                     : 'bg-white/10 group-hover:bg-white/20'">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                </svg>
              </div>
              <div class="flex-1 text-left">
                <span class="font-semibold text-white block">Dashboard</span>
                <span class="text-xs text-slate-300">Vista general</span>
              </div>
              <div v-if="activePanel === 'dashboard'" class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
            </button>
            
            <button @click="setActivePanel('tickets')" class="group w-full flex items-center space-x-4 px-4 py-3.5 rounded-xl transition-all duration-300 hover:translate-x-1"
                    :class="activePanel === 'tickets' 
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 shadow-lg border border-white/10 backdrop-blur-sm' 
                      : 'hover:bg-white/10 hover:shadow-md'">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300" 
                   :class="activePanel === 'tickets' 
                     ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                     : 'bg-white/10 group-hover:bg-white/20'">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
                </svg>
              </div>
              <div class="flex-1 text-left">
                <span class="font-semibold text-white block">Tickets</span>
                <span class="text-xs text-slate-300">Gestión completa</span>
              </div>
              <div v-if="activePanel === 'tickets'" class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
            </button>
            
            <button @click="setActivePanel('chat')" class="group w-full flex items-center space-x-4 px-4 py-3.5 rounded-xl transition-all duration-300 hover:translate-x-1"
                    :class="activePanel === 'chat' 
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 shadow-lg border border-white/10 backdrop-blur-sm' 
                      : 'hover:bg-white/10 hover:shadow-md'">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 relative" 
                   :class="activePanel === 'chat' 
                     ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                     : 'bg-white/10 group-hover:bg-white/20'">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <div class="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-slate-900 animate-pulse"></div>
              </div>
              <div class="flex-1 text-left">
                <span class="font-semibold text-white block">Elias AI</span>
                <span class="text-xs text-slate-300">Asistente virtual</span>
              </div>
              <div v-if="activePanel === 'chat'" class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
            </button>
            
            <button @click="setActivePanel('stats')" class="group w-full flex items-center space-x-4 px-4 py-3.5 rounded-xl transition-all duration-300 hover:translate-x-1"
                    :class="activePanel === 'stats' 
                      ? 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 shadow-lg border border-white/10 backdrop-blur-sm' 
                      : 'hover:bg-white/10 hover:shadow-md'">
              <div class="w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300" 
                   :class="activePanel === 'stats' 
                     ? 'bg-gradient-to-br from-blue-500 to-purple-600' 
                     : 'bg-white/10 group-hover:bg-white/20'">
                <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
              </div>
              <div class="flex-1 text-left">
                <span class="font-semibold text-white block">Estadísticas</span>
                <span class="text-xs text-slate-300">Análisis y reportes</span>
              </div>
              <div v-if="activePanel === 'stats'" class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></div>
            </button>
          </div>
        </nav>
        
        <!-- User section -->
        <div class="mt-auto pt-8">
          <div class="bg-white/5 rounded-xl p-4 mb-4 backdrop-blur-sm border border-white/10">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center font-bold text-sm">
                {{ authStore.user?.email ? authStore.user.email.charAt(0).toUpperCase() : 'U' }}
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-white font-medium text-sm truncate">{{ authStore.user?.name || authStore.user?.email || 'Usuario' }}</p>
                <p class="text-slate-300 text-xs truncate">{{ authStore.user?.email || 'user@ecomac.com' }}</p>
              </div>
            </div>
          </div>
          
          <button @click="logout" class="group w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-400/30 transition-all duration-300 hover:scale-[1.02]">
            <svg class="h-5 w-5 text-red-400 group-hover:text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
            <span class="font-medium text-red-400 group-hover:text-red-300">Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </aside>

<!-- Main Content -->
    <div class="flex-1 p-4 sm:p-6 lg:p-8 xl:p-12">
      <section v-if="activePanel === 'dashboard'" data-testid="dashboard">
        <!-- Header con estadísticas integradas -->
        <MetricsHeader
          title="Dashboard de Soporte"
          subtitle="Bienvenido a tu centro de control."
          icon="dashboard"
          @filter-priority="filterByPriority"
          @filter-status="filterByStatus"
        />
        

        <!-- Recent Tickets Section -->
        <div class="relative rounded-2xl bg-white shadow-lg border border-gray-200 overflow-hidden animate-fade-in-up" style="animation-delay: 0.5s;">
          <!-- Header compacto -->
          <div class="bg-gradient-to-r from-slate-50 to-blue-50/30 px-6 py-4 border-b border-gray-200">
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-md">
                  <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-lg font-bold text-slate-900">Tickets Recientes</h2>
                  <p class="text-slate-500 text-sm flex items-center">
                    <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                    </svg>
                    Semana {{ currentWeekRange }}
                  </p>
                </div>
              </div>
              
              <div class="flex items-center space-x-2">
                <div class="bg-white rounded-lg px-3 py-1.5 shadow-sm border border-gray-200">
                  <div class="flex items-center space-x-1.5">
                    <div class="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                    <span class="text-xs font-medium text-slate-700">{{ recentTickets.length }} activos</span>
                  </div>
                </div>
                
                <button 
                  @click="loadUserTickets" 
                  :disabled="isDashboardLoading"
                  class="group flex items-center space-x-1.5 px-4 py-2 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <svg :class="isDashboardLoading ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  <span class="font-medium">{{ isDashboardLoading ? 'Actualizando...' : 'Actualizar' }}</span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Loading state -->
          <div v-if="isDashboardLoading && recentTickets.length === 0" class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-ecomac-primary mx-auto mb-4"></div>
            <p class="text-gray-500">Cargando tickets recientes...</p>
          </div>
          
          <!-- No tickets state -->
          <div v-else-if="recentTickets.length === 0" class="text-center py-12">
            <div class="text-gray-400 mb-4">
              <svg class="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No hay tickets recientes</h3>
            <p class="text-gray-500 mb-6">No se encontraron tickets activos para la semana cursante.</p>
            <button @click="setActivePanel('chat')" class="px-6 py-2 bg-ecomac-primary text-white rounded-lg hover:bg-ecomac-dark transition-colors">
              Crear nuevo ticket
            </button>
          </div>
          
          <!-- Modern Tickets Table Reutilizada (Version Dashboard) -->
          <div v-else class="overflow-hidden" :class="{ 'transition-opacity duration-300': isDataStabilizing, 'opacity-95': isDataStabilizing }">
            
            <!-- Vista tabla - Dashboard siempre en tabla -->
            <div class="overflow-x-auto">
              <table class="w-full text-left">
                <!-- Table Header con diseño moderno -->
                <thead class="bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200">
                  <tr>
                    <th class="py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[60%]">Título</th>
                    <th class="py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[20%]">Asignado a</th>
                    <th class="py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[10%]">Progreso</th>
                    <th class="py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[10%]">Fecha</th>
                  </tr>
                </thead>
                
                <!-- Table Body con efectos modernos -->
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr 
                    v-for="(ticket, index) in recentTickets" 
                    :key="ticket.id" 
                    class="group hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-transparent transition-all duration-300 hover:scale-[1.01] hover:shadow-md animate-fade-in-up cursor-pointer"
                    :class="[index === 0 ? 'border-l-0' : 'border-l-4', index > 0 ? getStatusBorderClass(ticket.status) : '']"
                    :style="`animation-delay: ${(index + 1) * 50}ms;`"
                    @click="viewTicket(ticket.id)"
                    :title="`Clic para ver detalles del ticket ${ticket.id} - Estado: ${formatStatusText(ticket.status)}`"
                  >
                    
                    <!-- Título y descripción con indicador de estado -->
                    <td class="py-6 px-4">
                      <div class="max-w-2xl">
                        <div class="flex items-start gap-3">
                          <!-- Indicador circular de estado -->
                          <div class="flex-shrink-0 mt-1.5">
                            <div :class="getStatusIndicatorClass(ticket.status)" class="w-3 h-3 rounded-full shadow-sm" :title="formatStatusText(ticket.status)"></div>
                          </div>
                          
                          <!-- Contenido del título -->
                          <div class="flex-1 min-w-0">
                            <h4 class="text-sm font-semibold text-slate-900 line-clamp-1 group-hover:text-blue-600 transition-colors mb-1" :title="ticket.title">
                              {{ ticket.title }}
                            </h4>
                            <p class="text-xs text-slate-600 line-clamp-2 group-hover:text-slate-700 transition-colors mb-2" :title="ticket.description">
                              {{ ticket.description || 'Sin descripción' }}
                            </p>
                            <div class="flex items-center gap-2 flex-wrap">
                              <!-- Badge de estado -->
                              <span :class="TicketService.getStatusBadgeClass(ticket.status)" class="text-xs font-medium">
                                {{ formatStatusText(ticket.status) }}
                              </span>
                              <!-- Badge de prioridad con color dinámico -->
                              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :style="`background-color: ${getPriorityColor(ticket.priority)}15; color: ${getPriorityColor(ticket.priority)}`">
                                <div class="w-2 h-2 rounded-full mr-1" :style="`background-color: ${getPriorityColor(ticket.priority)}`"></div>
                                {{ formatPriorityText(ticket.priority) }}
                              </span>
                              <span v-if="getTicketMainTag(ticket)" class="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{{ getTicketMainTag(ticket) }}</span>
                              <!-- Badge de ID del ticket -->
                              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                #{{ ticket.id }}
                              </span>
                              <!-- Indicadores especiales -->
                              <span 
                                v-if="ticket.escalated" 
                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                              >
                                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                </svg>
                                Escalado
                              </span>
                              <span 
                                v-else-if="ticket.assignedTo === 'Elias AI'"
                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd" />
                                </svg>
                                Elias AI
                              </span>
                              <!-- Indicador de semana actual -->
                              <span 
                                v-if="isTicketInCurrentWeek(ticket)"
                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800"
                              >
                                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                </svg>
                                Esta semana
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    
                    <!-- Avatar y asignado -->
                    <td class="py-6 px-4">
                      <div class="flex items-center space-x-3">
                        <!-- Avatar mejorado para Elias AI -->
                        <div v-if="ticket.assignedTo === 'Elias AI'" class="relative">
                          <div class="w-9 h-9 rounded-xl overflow-hidden shadow-md ring-2 ring-blue-100 group-hover:ring-blue-200 transition-all duration-300 group-hover:scale-110 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 flex items-center justify-center">
                            <!-- Usar el avatar del webhook si está disponible, sino usar fallback -->
                            <img 
                              v-if="ticket.assignedToAvatar"
                              :src="ticket.assignedToAvatar" 
                              alt="Elias AI" 
                              class="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110"
                              @error="handleAvatarError"
                              @load="handleAvatarLoad"
                              style="display: block;"
                            >
                            <!-- Fallback avatar cuando no hay imagen o falla la carga -->
                            <div v-if="!ticket.assignedToAvatar || !avatarLoaded" class="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 flex items-center justify-center">
                              <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </div>
                          </div>
                          <!-- Indicator de AI activo -->
                          <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm">
                            <div class="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75"></div>
                          </div>
                        </div>
                        
                        <!-- Avatar para otros asignados -->
                        <div v-else class="relative">
                          <!-- Si tiene avatar del webhook, mostrar imagen -->
                          <div v-if="ticket.assignedToAvatar" class="w-9 h-9 rounded-xl overflow-hidden shadow-md ring-2 ring-slate-200 group-hover:ring-slate-300 transition-all duration-300 group-hover:scale-110">
                            <img 
                              :src="ticket.assignedToAvatar" 
                              :alt="ticket.assignedTo" 
                              class="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110"
                              @error="handleHumanAvatarError"
                              style="display: block;"
                            >
                            <!-- Fallback si falla la carga de imagen -->
                            <div class="absolute inset-0 bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center opacity-0 transition-opacity duration-300" :class="{'opacity-100': !avatarLoaded}">
                              <span class="text-xs font-bold text-white">{{ getAssigneeInitials(ticket.assignedTo) }}</span>
                            </div>
                          </div>
                          <!-- Si no tiene avatar, mostrar iniciales -->
                          <div v-else class="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                            <span class="text-xs font-bold text-white">{{ getAssigneeInitials(ticket.assignedTo) }}</span>
                          </div>
                          <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-gray-400 rounded-full border-2 border-white shadow-sm"></div>
                        </div>
                        
                        <!-- Información del asignado -->
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-slate-900 truncate group-hover:text-blue-600 transition-colors" :title="ticket.assignedTo">
                            {{ ticket.assignedTo || 'Sin asignar' }}
                          </p>
                          <p class="text-xs text-slate-500">
                            {{ ticket.assignedTo === 'Elias AI' ? 'Asistente Virtual' : 'Agente Humano' }}
                          </p>
                        </div>
                      </div>
                    </td>
                    
                    <!-- Progreso con círculo visual -->
                    <td class="py-6 px-4">
                      <div class="flex flex-col items-center">
                        <div class="w-16 h-16 relative mb-2">
                          <!-- Círculo de progreso -->
                          <svg class="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
                            <circle 
                              cx="32" cy="32" r="28" 
                              fill="none" stroke="#e5e7eb" stroke-width="4"
                            />
                            <circle 
                              cx="32" cy="32" r="28" 
                              fill="none" 
                              :stroke="getProgressColor(ticket.status)" 
                              stroke-width="4"
                              :stroke-dasharray="`${getProgressPercentage(ticket) * 1.759} 175.9`"
                              stroke-linecap="round"
                              class="transition-all duration-500"
                            />
                          </svg>
                          <!-- Porcentaje central -->
                          <div class="absolute inset-0 flex items-center justify-center">
                            <span class="text-sm font-bold text-slate-700">{{ getProgressPercentage(ticket) }}%</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <!-- Fecha consolidada -->
                    <td class="py-6 px-4">
                      <div class="flex flex-col space-y-1">
                        <div class="flex items-center space-x-1">
                          <svg class="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <p class="text-xs text-slate-900" :title="new Date(ticket.updatedAt).toLocaleString()">
                            {{ TicketService.formatDate(ticket.updatedAt) }}
                          </p>
                        </div>
                        <div class="flex items-center space-x-1">
                          <svg class="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                          </svg>
                          <p class="text-xs text-slate-500" :title="new Date(ticket.createdAt).toLocaleString()">
                            {{ TicketService.formatDate(ticket.createdAt) }}
                          </p>
                        </div>
                        <a v-if="ticket.clickupUrl" :href="ticket.clickupUrl" target="_blank" class="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1 mt-1">
                          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                          </svg>
                          <span>ClickUp</span>
                        </a>
                      </div>
                    </td>
                    
                  </tr>
                </tbody>
              </table>
              </div>
            </div>
            
            <!-- Paginación del Dashboard -->
            <div v-if="dashboardTotalPages > 1" class="bg-gradient-to-r from-slate-50 to-blue-50/30 border-t border-gray-200 px-6 py-4">
              <div class="flex items-center justify-between">
                <!-- Información de paginación -->
                <div class="flex items-center text-sm text-gray-700">
                  <span>Mostrando {{ dashboardPaginationInfo.start }} - {{ dashboardPaginationInfo.end }} de {{ dashboardPaginationInfo.total }} tickets recientes</span>
                </div>
                
                <!-- Controles de paginación compactos -->
                <div class="flex items-center space-x-1">
                  <!-- Botón Anterior -->
                  <button 
                    @click="goToDashboardPage(dashboardCurrentPage - 1)"
                    :disabled="dashboardCurrentPage === 1"
                    class="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                    </svg>
                    Anterior
                  </button>
                  
                  <!-- Números de página (compacto para dashboard) -->
                  <div class="flex items-center space-x-1">
                    <template v-for="page in getDashboardVisiblePages()" :key="page">
                      <button 
                        v-if="page !== '...'"
                        @click="goToDashboardPage(page)"
                        :class="[
                          'px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                          page === dashboardCurrentPage 
                            ? 'bg-blue-600 text-white shadow-lg' 
                            : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                        ]"
                      >
                        {{ page }}
                      </button>
                      <span v-else class="px-2 py-2 text-gray-500">...</span>
                    </template>
                  </div>
                  
                  <!-- Botón Siguiente -->
                  <button 
                    @click="goToDashboardPage(dashboardCurrentPage + 1)"
                    :disabled="dashboardCurrentPage === dashboardTotalPages"
                    class="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                  >
                    Siguiente
                    <svg class="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Enhanced Show More Section -->
            <div class="bg-gradient-to-r from-slate-50 to-blue-50/30 border-t border-gray-200 px-8 py-6">
              <div class="text-center">
                <button 
                  @click="setActivePanel('tickets')" 
                  class="group inline-flex items-center space-x-3 px-8 py-4 text-sm font-semibold text-slate-700 hover:text-blue-600 bg-white hover:bg-blue-50 border border-gray-200 hover:border-blue-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:scale-[1.02]"
                >
                  <span>Ver todos los tickets</span>
                  <div class="w-6 h-6 rounded-lg bg-blue-500 text-white flex items-center justify-center group-hover:bg-blue-600 group-hover:rotate-12 transition-all duration-300">
                    <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7"/>
                    </svg>
                  </div>
                </button>
                <p class="text-xs text-slate-500 mt-3">
                  {{ allRecentTickets.length }} ticket{{ allRecentTickets.length > 1 ? 's' : '' }} activo{{ allRecentTickets.length > 1 ? 's' : '' }} total
                </p>
              </div>
            </div>
        </div>
      </section>

      <section v-if="activePanel === 'tickets'" data-testid="tickets">
        <!-- Header con estadísticas integradas -->
        <MetricsHeader
          title="Gestión de Tickets"
          subtitle="Administra y da seguimiento a todos los tickets de soporte"
          icon="tickets"
          @filter-priority="filterByPriority"
          @filter-status="filterByStatus"
        />
        
        <!-- Tickets Management Section -->
        <div class="relative rounded-2xl bg-white shadow-lg border border-gray-200 overflow-hidden animate-fade-in-up" style="animation-delay: 0.5s;">
          <!-- Header compacto -->
          <div class="bg-gradient-to-r from-slate-50 to-blue-50/30 px-4 sm:px-6 py-4 border-b border-gray-200">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div class="flex items-center space-x-3">
                <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center shadow-md">
                  <svg class="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
                  </svg>
                </div>
                <div>
                  <h2 class="text-lg font-bold text-slate-900">Todos los Tickets</h2>
                  <p class="text-slate-500 text-sm flex items-center">
                    <svg class="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14-7H5m14 14H5"/>
                    </svg>
                    Historial completo de soporte
                  </p>
                </div>
              </div>
              
              <div class="flex items-center justify-between sm:justify-end gap-3">
                <div class="bg-white rounded-lg px-3 py-1.5 shadow-sm border border-gray-200 flex-shrink-0">
                  <div class="flex items-center space-x-1.5">
                    <div class="w-1.5 h-1.5 bg-blue-400 rounded-full animate-pulse"></div>
                    <span class="text-xs font-medium text-slate-700">{{ ticketStats.total }} tickets</span>
                  </div>
                </div>
                
                <div class="flex items-center gap-2">
                  <!-- Botón de filtros solo visible en móviles/tablets -->
                  <button 
                    @click="showFilters = !showFilters"
                    class="lg:hidden group flex items-center space-x-1.5 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02] text-sm flex-shrink-0"
                    :title="showFilters ? 'Ocultar filtros' : 'Mostrar filtros'"
                  >
                    <svg 
                      class="w-4 h-4 transition-transform duration-200" 
                      :class="{ 'rotate-180': showFilters }"
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 2v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z" />
                    </svg>
                    <span class="font-medium">Filtros</span>
                  </button>
                  
                  <button 
                    @click="loadUserTickets" 
                    :disabled="isLoadingTickets"
                    class="group flex items-center space-x-1.5 px-4 py-2 bg-gradient-to-r from-slate-700 to-slate-800 hover:from-slate-800 hover:to-slate-900 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-sm flex-shrink-0"
                  >
                    <svg :class="isLoadingTickets ? 'animate-spin' : 'group-hover:rotate-180 transition-transform duration-500'" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                    </svg>
                    <span class="font-medium">{{ isLoadingTickets ? 'Actualizando...' : 'Actualizar' }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Controles de Filtrado y Vista -->
          <!-- Filtros siempre visibles en desktop, colapsables en móviles/tablets -->
          <div class="bg-white border-b border-gray-200" :class="showFilters || windowWidth >= 1024 ? 'px-8 py-6' : 'px-8 py-0'">
            <div v-if="showFilters || windowWidth >= 1024" class="flex flex-col lg:flex-row gap-6 items-start lg:items-center justify-between animate-fadeIn">
              <!-- Búsqueda mejorada -->
              <div class="relative flex-1 w-full md:max-w-md">
                <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                  </svg>
                </div>
                <input 
                  type="text" 
                  placeholder="Buscar por título, ID o descripción..." 
                  v-model="searchQuery"
                  class="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-ecomac-primary focus:border-transparent shadow-sm transition-all duration-200 hover:shadow-md"
                >
                <!-- Clear button -->
                <button 
                  v-if="searchQuery" 
                  @click="searchQuery = ''"
                  class="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                  </svg>
                </button>
              </div>
              
              <!-- Filtros y controles mejorados -->
              <div class="flex flex-col lg:flex-row flex-wrap gap-4 items-start lg:items-center w-full lg:w-auto">
                <!-- Filtro de estado -->
                <div class="relative w-full lg:w-auto min-w-0">
                  <select 
                    v-model="filterStatus"
                    class="w-full appearance-none bg-white border border-gray-300 rounded-xl px-4 py-3 pr-8 focus:ring-2 focus:ring-ecomac-primary focus:border-transparent shadow-sm hover:shadow-md transition-all duration-200 text-sm font-medium lg:min-w-[200px]"
                  >
                    <option value="">Todos los estados</option>
                    <option value="abierto">🔴 Abierto</option>
                    <option value="en_progreso">🟡 En Progreso</option>
                    <option value="cerrado">🟢 Cerrado</option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>
                
                <!-- Filtro de prioridad -->
                <div class="relative w-full lg:w-auto min-w-0">
                  <select 
                    v-model="filterPriority"
                    class="w-full appearance-none bg-white border border-gray-300 rounded-xl px-4 py-3 pr-8 focus:ring-2 focus:ring-ecomac-primary focus:border-transparent shadow-sm hover:shadow-md transition-all duration-200 text-sm font-medium lg:min-w-[200px]"
                  >
                    <option value="">Todas las prioridades</option>
                    <option value="alta">🔥 Alta</option>
                    <option value="media">⚠️ Media</option>
                    <option value="baja">🟢 Baja</option>
                  </select>
                  <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                    </svg>
                  </div>
                </div>
                
                <!-- Toggle vista - Solo visible en desktop -->
                <div class="hidden lg:flex bg-gray-100 rounded-xl p-1 shadow-sm">
                  <button 
                    @click="viewMode = 'table'"
                    :class="viewMode === 'table' ? 'bg-white shadow-sm text-ecomac-primary' : 'text-gray-500 hover:text-gray-700'"
                    class="px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium"
                  >
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"/>
                    </svg>
                    Tabla
                  </button>
                  <button 
                    @click="viewMode = 'cards'"
                    :class="viewMode === 'cards' ? 'bg-white shadow-sm text-ecomac-primary' : 'text-gray-500 hover:text-gray-700'"
                    class="px-4 py-2 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium"
                  >
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14-7H5m14 14H5"/>
                    </svg>
                    Cards
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Estado de carga -->
          <div v-if="isLoadingTickets && ticketsList.length === 0" class="text-center py-8">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-ecomac-primary mx-auto mb-4"></div>
            <p class="text-gray-500">Cargando todos los tickets...</p>
          </div>
          
          <!-- Error de carga -->
          <div v-else-if="ticketsError" class="text-center py-8">
            <div class="text-red-500 mb-4">
              <svg class="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p class="text-red-600 font-medium mb-2">Error al cargar tickets</p>
            <p class="text-gray-500 mb-4">{{ ticketsError }}</p>
            <button @click="loadUserTickets" class="px-6 py-2 bg-ecomac-primary text-white rounded-lg hover:bg-ecomac-dark transition-colors">Reintentar</button>
          </div>
          
          <!-- Sin tickets -->
          <div v-else-if="ticketsList.length === 0" class="text-center py-12">
            <div class="text-gray-400 mb-4">
              <svg class="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"/>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No hay tickets disponibles</h3>
            <p class="text-gray-500 mb-6">Aún no has creado ningún ticket de soporte.</p>
            <button @click="setActivePanel('chat')" class="px-6 py-2 bg-ecomac-primary text-white rounded-lg hover:bg-ecomac-dark transition-colors">
              Crear primer ticket con Elias AI
            </button>
          </div>
          
          <!-- Sin resultados en filtros -->
          <div v-else-if="allFilteredTickets.length === 0" class="text-center py-12">
            <div class="text-gray-400 mb-4">
              <svg class="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-900 mb-2">No se encontraron tickets</h3>
            <p class="text-gray-500 mb-6">No hay tickets que coincidan con los filtros seleccionados.</p>
            <button @click="searchQuery = ''; filterStatus = ''; filterPriority = ''; resetPagination();" class="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
              Limpiar filtros
            </button>
          </div>

          <!-- Modern Tickets Table/Cards -->
          <div v-else class="overflow-hidden" :class="{ 'transition-opacity duration-300': isDataStabilizing, 'opacity-95': isDataStabilizing }">
            <!-- Vista tabla - Solo en desktop, fallback a cards en mobile -->
            <div v-if="viewMode === 'table' && !isMobileView" class="overflow-x-auto">
              <table class="w-full text-left">
                <!-- Table Header con diseño moderno -->
                <thead class="bg-gradient-to-r from-gray-50 to-slate-50 border-b border-gray-200">
                  <tr>
                    <th class="py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[60%]">Título</th>
                    <th class="py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[20%]">Asignado a</th>
                    <th class="py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[10%]">Progreso</th>
                    <th class="py-4 px-4 text-xs font-semibold text-gray-600 uppercase tracking-wider w-[10%]">Fecha</th>
                  </tr>
                </thead>
                <!-- Table Body con efectos modernos -->
                <tbody class="bg-white divide-y divide-gray-100">
                  <tr 
                    v-for="(ticket, index) in filteredTickets" 
                    :key="ticket.id" 
                    class="group hover:bg-gradient-to-r hover:from-blue-50/30 hover:to-transparent transition-all duration-300 hover:scale-[1.01] hover:shadow-md animate-fade-in-up cursor-pointer"
                    :class="[index === 0 ? 'border-l-0' : 'border-l-4', index > 0 ? getStatusBorderClass(ticket.status) : '']"
                    :style="`animation-delay: ${(index + 1) * 50}ms;`"
                    @click="viewTicket(ticket.id)"
                    :title="`Clic para ver detalles del ticket ${ticket.id} - Estado: ${formatStatusText(ticket.status)}`"
                  >
                    
                    <!-- Título y descripción con indicador de estado -->
                    <td class="py-6 px-4">
                      <div class="max-w-2xl">
                        <div class="flex items-start gap-3">
                          <!-- Indicador circular de estado -->
                          <div class="flex-shrink-0 mt-1.5">
                            <div :class="getStatusIndicatorClass(ticket.status)" class="w-3 h-3 rounded-full shadow-sm" :title="formatStatusText(ticket.status)"></div>
                          </div>
                          
                          <!-- Contenido del título -->
                          <div class="flex-1 min-w-0">
                            <h4 class="text-sm font-semibold text-slate-900 line-clamp-1 group-hover:text-blue-600 transition-colors mb-1" :title="ticket.title">
                              {{ ticket.title }}
                            </h4>
                            <p class="text-xs text-slate-600 line-clamp-2 group-hover:text-slate-700 transition-colors mb-2" :title="ticket.description">
                              {{ ticket.description }}
                            </p>
                            <div class="flex items-center gap-2 flex-wrap">
                              <!-- Badge de estado -->
                              <span :class="TicketService.getStatusBadgeClass(ticket.status)" class="text-xs font-medium">
                                {{ formatStatusText(ticket.status) }}
                              </span>
                              <!-- Badge de prioridad con color dinámico -->
                              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium" :style="`background-color: ${getPriorityColor(ticket.priority)}15; color: ${getPriorityColor(ticket.priority)}`">
                                <div class="w-2 h-2 rounded-full mr-1" :style="`background-color: ${getPriorityColor(ticket.priority)}`"></div>
                                {{ formatPriorityText(ticket.priority) }}
                              </span>
                              <span v-if="getTicketMainTag(ticket)" class="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full">{{ getTicketMainTag(ticket) }}</span>
                              <span 
                                v-if="ticket.escalated" 
                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                              >
                                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                                </svg>
                                Escalado
                              </span>
                              <span 
                                v-else 
                                class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd" />
                                </svg>
                                Elias AI
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    
                    <!-- Avatar y asignado -->
                    <td class="py-6 px-4">
                      <div class="flex items-center space-x-3">
                        <!-- Avatar mejorado para Elias AI -->
                        <div v-if="ticket.assignedTo === 'Elias AI'" class="relative">
                          <div class="w-9 h-9 rounded-xl overflow-hidden shadow-md ring-2 ring-blue-100 group-hover:ring-blue-200 transition-all duration-300 group-hover:scale-110 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 flex items-center justify-center">
                            <!-- Usar el avatar del webhook si está disponible, sino usar fallback -->
                            <img 
                              v-if="ticket.assignedToAvatar"
                              :src="ticket.assignedToAvatar" 
                              alt="Elias AI" 
                              class="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110"
                              @error="handleAvatarError"
                              @load="handleAvatarLoad"
                              style="display: block;"
                            >
                            <!-- Fallback avatar cuando no hay imagen o falla la carga -->
                            <div v-if="!ticket.assignedToAvatar || !avatarLoaded" class="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 flex items-center justify-center">
                              <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                              </svg>
                            </div>
                          </div>
                          <!-- Indicator de AI activo -->
                          <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full border-2 border-white shadow-sm">
                            <div class="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75"></div>
                          </div>
                        </div>
                        
                        <!-- Avatar para otros asignados -->
                        <div v-else class="relative">
                          <!-- Si tiene avatar del webhook, mostrar imagen -->
                          <div v-if="ticket.assignedToAvatar" class="w-9 h-9 rounded-xl overflow-hidden shadow-md ring-2 ring-slate-200 group-hover:ring-slate-300 transition-all duration-300 group-hover:scale-110">
                            <img 
                              :src="ticket.assignedToAvatar" 
                              :alt="ticket.assignedTo" 
                              class="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110"
                              @error="handleHumanAvatarError"
                              style="display: block;"
                            >
                            <!-- Fallback si falla la carga de imagen -->
                            <div class="absolute inset-0 bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center opacity-0 transition-opacity duration-300" :class="{'opacity-100': !avatarLoaded}">
                              <span class="text-xs font-bold text-white">{{ getAssigneeInitials(ticket.assignedTo) }}</span>
                            </div>
                          </div>
                          <!-- Si no tiene avatar, mostrar iniciales -->
                          <div v-else class="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                            <span class="text-xs font-bold text-white">{{ getAssigneeInitials(ticket.assignedTo) }}</span>
                          </div>
                          <div class="absolute -bottom-1 -right-1 w-3 h-3 bg-gray-400 rounded-full border-2 border-white shadow-sm"></div>
                        </div>
                        
                        <!-- Información del asignado -->
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-slate-900 truncate group-hover:text-blue-600 transition-colors" :title="ticket.assignedTo">
                            {{ ticket.assignedTo || 'Sin asignar' }}
                          </p>
                          <p class="text-xs text-slate-500">
                            {{ ticket.assignedTo === 'Elias AI' ? 'Asistente Virtual' : 'Agente Humano' }}
                          </p>
                        </div>
                      </div>
                    </td>
                    
                    <!-- Progreso con círculo visual -->
                    <td class="py-6 px-4">
                      <div class="flex flex-col items-center">
                        <div class="w-16 h-16 relative mb-2">
                          <!-- Círculo de progreso -->
                          <svg class="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
                            <circle 
                              cx="32" cy="32" r="28" 
                              fill="none" stroke="#e5e7eb" stroke-width="4"
                            />
                            <circle 
                              cx="32" cy="32" r="28" 
                              fill="none" 
                              :stroke="getProgressColor(ticket.status)" 
                              stroke-width="4"
                              :stroke-dasharray="`${getProgressPercentage(ticket) * 1.759} 175.9`"
                              stroke-linecap="round"
                              class="transition-all duration-500"
                            />
                          </svg>
                          <!-- Porcentaje central -->
                          <div class="absolute inset-0 flex items-center justify-center">
                            <span class="text-sm font-bold text-slate-700">{{ getProgressPercentage(ticket) }}%</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <!-- Fecha consolidada -->
                    <td class="py-6 px-4">
                      <div class="flex flex-col space-y-1">
                        <div class="flex items-center space-x-1">
                          <svg class="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                          </svg>
                          <p class="text-xs text-slate-900" :title="new Date(ticket.updatedAt).toLocaleString()">
                            {{ TicketService.formatDate(ticket.updatedAt) }}
                          </p>
                        </div>
                        <div class="flex items-center space-x-1">
                          <svg class="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                          </svg>
                          <p class="text-xs text-slate-500" :title="new Date(ticket.createdAt).toLocaleString()">
                            {{ TicketService.formatDate(ticket.createdAt) }}
                          </p>
                        </div>
                        <a v-if="ticket.clickupUrl" :href="ticket.clickupUrl" target="_blank" class="text-xs text-blue-600 hover:text-blue-800 flex items-center space-x-1 mt-1">
                          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                          </svg>
                          <span>ClickUp</span>
                        </a>
                      </div>
                    </td>
                    
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Vista cards - Visible cuando es seleccionada o en dispositivos móviles -->
            <div v-if="viewMode === 'cards' || (viewMode === 'table' && isMobileView)" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
            <div 
              v-for="(ticket, index) in filteredTickets" 
              :key="ticket.id" 
              class="bg-white rounded-2xl shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] overflow-hidden group animate-fade-in-up"
              :style="`animation-delay: ${index * 100}ms;`"
            >
              <!-- Card header -->
              <div class="relative p-6 pb-4">
                <!-- Ticket priority indicator -->
                <div class="absolute top-0 right-0 w-full h-1">
                  <div 
                    :class="getPriorityGradientClass(ticket.priority)"
                    class="h-full transition-all duration-300"
                  ></div>
                </div>
                
                <!-- Avatar y status -->
                <div class="flex items-start justify-between mb-4">
                  <div class="flex items-center space-x-3">
                    <!-- Avatar -->
                    <div class="relative">
                    <!-- Avatar de Elias AI -->
                    <div v-if="ticket.assignedTo === 'Elias AI'" class="w-12 h-12 rounded-xl overflow-hidden shadow-md ring-2 ring-blue-100/50 relative group transition-all duration-300 hover:scale-105">
                      <img 
                        v-if="ticket.assignedToAvatar"
                        :src="ticket.assignedToAvatar" 
                        alt="Elias AI" 
                        class="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110"
                        @error="handleAvatarError"
                      />
                      <!-- Fallback cuando no hay avatar -->
                      <div v-else class="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-xl flex items-center justify-center">
                        <svg class="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      <!-- Animated background -->
                      <div class="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                      
                      <!-- Avatar para agentes humanos -->
                      <div v-else class="relative">
                        <!-- Si tiene avatar del webhook, mostrar imagen -->
                        <div v-if="ticket.assignedToAvatar" class="w-12 h-12 rounded-xl overflow-hidden shadow-md ring-2 ring-slate-200 group-hover:ring-slate-300 transition-all duration-300 hover:scale-105">
                          <img 
                            :src="ticket.assignedToAvatar" 
                            :alt="ticket.assignedTo" 
                            class="w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110"
                            @error="handleHumanAvatarError"
                            style="display: block;"
                          >
                          <!-- Fallback si falla la carga de imagen -->
                          <div class="absolute inset-0 bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center opacity-0 transition-opacity duration-300" :class="{'opacity-100': !avatarLoaded}">
                            <span class="text-xs font-bold text-white">{{ getAssigneeInitials(ticket.assignedTo) }}</span>
                          </div>
                        </div>
                        <!-- Si no tiene avatar, mostrar iniciales -->
                        <div v-else class="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-600 to-slate-700 flex items-center justify-center text-white font-bold text-lg shadow-md transition-all duration-300 hover:scale-105">
                          <span class="text-sm font-bold text-white">{{ getAssigneeInitials(ticket.assignedTo) }}</span>
                        </div>
                        <!-- Status indicator -->
                        <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-gray-400 rounded-full border-2 border-white shadow-sm"></div>
                      </div>
                      
                      <!-- Status indicator -->
                      <div :class="getStatusIndicatorClass(ticket.status)" class="absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm">
                      </div>
                    </div>
                    
                    <!-- ID y origen -->
                    <div>
                      <p class="text-sm font-mono text-gray-600 mb-1">#{{ ticket.id }}</p>
                      <span 
                        v-if="ticket.escalated" 
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800"
                      >
                        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                        </svg>
                        Escalado
                      </span>
                      <span 
                        v-else 
                        class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                      >
                        <svg class="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd" />
                        </svg>
                        Elias AI
                      </span>
                    </div>
                  </div>
                  
                  <!-- Priority badge -->
                  <div class="flex flex-col items-end">
                    <span :class="TicketService.getPriorityBadgeClass(ticket.priority)" class="mb-2">
                      {{ formatPriorityText(ticket.priority) }}
                    </span>
                  </div>
                </div>
                
                <!-- Título y descripción -->
                <div class="mb-4">
                  <h3 class="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-ecomac-primary transition-colors">
                    {{ ticket.title }}
                  </h3>
                  <p class="text-sm text-gray-600 line-clamp-3">
                    {{ ticket.description }}
                  </p>
                </div>
                
                <!-- Progress bar -->
                <div class="mb-4">
                  <div class="flex items-center justify-between mb-2">
                    <span class="text-xs font-medium text-gray-700">Progreso</span>
                    <span class="text-xs text-gray-500">{{ getProgressPercentage(ticket) }}%</span>
                  </div>
                  <div class="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      :class="getProgressBarClass(ticket.status)"
                      class="h-2 rounded-full transition-all duration-500 ease-out"
                      :style="`width: ${getProgressPercentage(ticket)}%`"
                    ></div>
                  </div>
                </div>
              </div>
              
              <!-- Card body -->
              <div class="px-6 pb-4">
                <!-- Status y asignado -->
                <div class="flex items-center justify-between mb-4">
                  <div>
                    <span class="text-xs text-gray-500 block mb-1">Estado</span>
                    <span :class="TicketService.getStatusBadgeClass(ticket.status)">
                      {{ formatStatusText(ticket.status) }}
                    </span>
                  </div>
                  <div class="text-right">
                    <span class="text-xs text-gray-500 block mb-1">Asignado a</span>
                    <span class="text-sm font-medium text-gray-900">{{ ticket.assignedTo || 'Sin asignar' }}</span>
                  </div>
                </div>
                
                <!-- Fechas y origen -->
                <div class="mb-4">
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <span class="text-xs text-gray-500 block mb-1">Creado</span>
                      <div class="flex items-center space-x-1">
                        <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                        </svg>
                        <span class="text-xs text-gray-600">{{ TicketService.formatDate(ticket.createdAt) }}</span>
                      </div>
                    </div>
                    <div>
                      <span class="text-xs text-gray-500 block mb-1">Actualizado</span>
                      <div class="flex items-center space-x-1">
                        <svg class="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <span class="text-xs text-gray-600">{{ TicketService.formatDate(ticket.updatedAt) }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Información adicional -->
                  <div class="mt-3 space-y-2">
                    <div v-if="ticket.source" class="flex items-center justify-between">
                      <span class="text-xs text-gray-500">Fuente:</span>
                      <span class="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">{{ ticket.source }}</span>
                    </div>
                    
                    <div v-if="ticket.requesterName && ticket.requesterName !== ticket.userEmail" class="flex items-center justify-between">
                      <span class="text-xs text-gray-500">Solicitante:</span>
                      <span class="text-xs text-gray-700 font-medium">{{ ticket.requesterName }}</span>
                    </div>
                    
                    <div v-if="ticket.tags && ticket.tags.length > 0" class="flex flex-wrap gap-1 mt-2">
                      <span v-for="tag in ticket.tags.slice(0, 3)" :key="tag" class="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {{ tag }}
                      </span>
                      <span v-if="ticket.tags.length > 3" class="text-xs text-gray-500">+{{ ticket.tags.length - 3 }} más</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Card footer -->
              <div class="px-6 py-4 bg-gray-50 border-t border-gray-100">
                <div class="flex items-center justify-between">
                  <div class="flex items-center space-x-2">
                    <button 
                      @click="viewTicket(ticket.id)" 
                      class="flex items-center space-x-1 text-ecomac-primary hover:text-ecomac-dark text-sm font-medium transition-colors group/btn"
                      :data-testid="`view-ticket-card-${ticket.id}`"
                      :aria-label="`Ver detalles del ticket ${ticket.id}`"
                    >
                      <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                      <span>Ver</span>
                    </button>
                    
                    <button 
                      @click="editTicket(ticket)" 
                      class="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors group/btn"
                    >
                      <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                      <span>Editar</span>
                    </button>
                  </div>
                  
                  <!-- Link a ClickUp -->
                  <a v-if="ticket.clickupUrl" :href="ticket.clickupUrl" target="_blank" class="flex items-center space-x-1 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors group/btn">
                    <svg class="w-4 h-4 group-hover/btn:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                    </svg>
                    <span class="text-xs">ClickUp</span>
                  </a>
                </div>
              </div>
              </div>
            </div>
          </div>
          
          <!-- Paginación -->
          <div v-if="totalPages > 1" class="bg-white border-t border-gray-200 px-6 py-4">
            <div class="flex items-center justify-between">
              <!-- Información de paginación -->
              <div class="flex items-center text-sm text-gray-700">
                <span>Mostrando {{ paginationInfo.start }} - {{ paginationInfo.end }} de {{ paginationInfo.total }} tickets</span>
              </div>
              
              <!-- Controles de paginación -->
              <div class="flex items-center space-x-2">
                <!-- Botón Anterior -->
                <button 
                  @click="goToPage(currentPage - 1)"
                  :disabled="currentPage === 1"
                  class="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  <svg class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                  Anterior
                </button>
                
                <!-- Números de página -->
                <div class="flex items-center space-x-1">
                  <template v-for="page in getVisiblePages()" :key="page">
                    <button 
                      v-if="page !== '...'"
                      @click="goToPage(page)"
                      :class="[
                        'px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                        page === currentPage 
                          ? 'bg-blue-600 text-white shadow-lg' 
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                      ]"
                    >
                      {{ page }}
                    </button>
                    <span v-else class="px-2 py-2 text-gray-500">...</span>
                  </template>
                </div>
                
                <!-- Botón Siguiente -->
                <button 
                  @click="goToPage(currentPage + 1)"
                  :disabled="currentPage === totalPages"
                  class="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                >
                  Siguiente
                  <svg class="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="activePanel === 'chat'" class="h-full flex flex-col animate-fade-in-up">
        <!-- Chat Container Moderno -->
<div class="flex-1 flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50/30 rounded-3xl shadow-2xl border border-slate-200/60 overflow-hidden backdrop-blur-sm h-full">
          <!-- Header Premium del Chat -->
          <div class="relative bg-gradient-to-r from-slate-900 via-blue-900 to-indigo-900 p-6 shadow-2xl">
            <!-- Elementos decorativos del header -->
            <div class="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-blue-900/90 to-indigo-900/95 backdrop-blur-xl"></div>
            <div class="absolute top-0 right-0 w-96 h-32 bg-gradient-conic from-blue-400/20 via-purple-400/20 to-indigo-400/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
            <div class="absolute bottom-0 left-0 w-64 h-24 bg-gradient-conic from-cyan-400/15 via-blue-400/15 to-slate-400/15 rounded-full blur-2xl transform -translate-x-1/2 translate-y-1/2"></div>
            
            <!-- Contenido del header -->
            <div class="relative z-10 flex items-center justify-between">
              <div class="flex items-center space-x-6">
                <!-- Avatar Premium de Elias AI -->
                <div class="relative group">
                  <div class="w-16 h-16 rounded-3xl bg-gradient-to-br from-blue-400 via-blue-500 to-indigo-600 flex items-center justify-center shadow-2xl border border-white/20 group-hover:scale-105 transition-all duration-500">
                    <div class="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/10 to-transparent opacity-50"></div>
                    <svg class="w-8 h-8 text-white relative z-10 drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <!-- Indicador de estado online animado -->
                  <div class="absolute -bottom-1 -right-1 w-5 h-5 bg-green-400 rounded-full border-2 border-white shadow-lg">
                    <div class="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75"></div>
                    <div class="absolute inset-1 bg-green-300 rounded-full animate-pulse"></div>
                  </div>
                </div>
                
                <!-- Información del AI -->
                <div>
                  <div class="flex items-center space-x-3 mb-2">
                    <h2 class="text-2xl font-bold text-white flex items-center">
                      <span class="bg-gradient-to-r from-white via-blue-100 to-cyan-100 bg-clip-text text-transparent">Elias AI</span>
                      <svg class="w-6 h-6 ml-2 text-blue-300" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                      </svg>
                    </h2>
                  </div>
                  <div class="flex items-center space-x-4 text-blue-100">
                    <div class="flex items-center space-x-2">
                      <div :class="connectionIndicatorClass" class="w-2 h-2 rounded-full shadow-lg"></div>
                      <span class="text-sm font-medium">{{ connectionStatus }}</span>
                    </div>
                    <div v-if="conversationHistory.length > 0" class="flex items-center space-x-2 text-blue-200">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                      </svg>
                      <span class="text-sm">{{ conversationHistory.length }} mensajes</span>
                    </div>
                    <div v-if="responseTime" class="flex items-center space-x-2 text-green-200">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                      </svg>
                      <span class="text-sm font-medium">{{ responseTime }}ms</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Controles del Header -->
              <div class="flex items-center space-x-3">
                <!-- Indicador de escalamiento premium -->
                <div v-if="isEscalated" class="bg-gradient-to-r from-orange-500/20 to-red-500/20 backdrop-blur-sm border border-orange-400/30 text-orange-100 px-4 py-2 rounded-2xl shadow-xl">
                  <div class="flex items-center space-x-2">
                    <svg class="w-5 h-5 text-orange-300" fill="currentColor" viewBox="0 0 20 20">
                      <path fill-rule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                    <span class="text-sm font-bold">Escalado a Humano</span>
                  </div>
                </div>
                
                <!-- Botones premium -->
                <button 
                  @click="clearChat" 
                  class="group flex items-center space-x-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/30 text-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  title="Limpiar conversación"
                >
                  <svg class="w-4 h-4 group-hover:rotate-12 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span class="hidden sm:inline text-sm font-medium">Limpiar</span>
                </button>
                
                <button 
                  @click="startNewSession" 
                  class="group flex items-center space-x-2 px-6 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 border border-blue-400/20"
                  title="Iniciar nueva sesión"
                >
                  <svg class="w-4 h-4 group-hover:rotate-180 transition-transform duration-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span class="hidden sm:inline text-sm font-bold">Nueva Sesión</span>
                </button>
              </div>
            </div>
          </div>

          <!-- Área de Mensajes Rediseñada con Performance Monitor -->
          <div 
            ref="messagesContainer" 
            class="flex-1 overflow-y-auto bg-gradient-to-b from-slate-50/50 to-white relative"
            :class="{
              'overflow-hidden': isEliasTyping
            }"
          >
            <!-- Fondo decorativo sutil -->
            <div class="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(59,130,246,0.05),transparent_50%),radial-gradient(circle_at_70%_80%,rgba(147,51,234,0.05),transparent_50%)] pointer-events-none"></div>
            
            <!-- Banner de escalamiento -->
            <EscalationBanner 
              :is-escalated="isEscalated"
              :escalation-time="escalationTime"
              :assigned-agent="assignedAgent"
              :priority="escalationPriority"
              :ticket-id="escalationTicketId"
              :estimated-response-time="estimatedResponseTime"
              @view-ticket="handleViewEscalationTicket"
              @dismiss="handleDismissEscalationBanner"
            />
            
            <!-- Contenido de mensajes -->
            <div class="relative z-10 p-6 space-y-6">
              <!-- Mensaje de Bienvenida Premium -->
              <div v-if="messages.length === 0" class="flex flex-col items-center text-center py-16 px-8">
                <div class="relative mb-8">
                  <!-- Avatar principal de bienvenida -->
                  <div class="w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-700 flex items-center justify-center shadow-2xl border-4 border-white">
                    <svg class="w-12 h-12 text-white drop-shadow-lg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <!-- Efectos de partículas alrededor del avatar -->
                  <div class="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-bounce" style="animation-delay: 0.5s;"></div>
                  <div class="absolute -bottom-2 -left-2 w-4 h-4 bg-gradient-to-r from-green-400 to-cyan-500 rounded-full animate-bounce" style="animation-delay: 1s;"></div>
                  <div class="absolute top-1/2 -right-4 w-3 h-3 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full animate-pulse" style="animation-delay: 1.5s;"></div>
                </div>
                
                <h3 class="text-3xl font-bold text-slate-900 mb-4 flex items-center">
                  <span class="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">¡Hola! Soy Elias AI</span>
                  <span class="ml-3 text-2xl animate-wave">👋</span>
                </h3>
                
                <p class="text-lg text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Soy tu asistente de inteligencia artificial especializado en soporte técnico.
                  <span class="font-medium text-blue-600">Estoy aquí para ayudarte</span> a resolver cualquier problema técnico que tengas.
                </p>
                
                <!-- Sugerencias de inicio -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                  <button 
                    @click="currentMessage = 'Tengo problemas para iniciar sesión'; sendMessage();"
                    class="group p-4 bg-white hover:bg-blue-50 border border-slate-200 hover:border-blue-300 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    <div class="flex items-center space-x-3">
                      <div class="w-10 h-10 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center group-hover:rotate-6 transition-transform">
                        <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div class="text-left">
                        <p class="text-sm font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">Problemas de Login</p>
                        <p class="text-xs text-slate-500">No puedo acceder al sistema</p>
                      </div>
                    </div>
                  </button>
                  
                  <button 
                    @click="currentMessage = 'El sistema funciona muy lento'; sendMessage();"
                    class="group p-4 bg-white hover:bg-yellow-50 border border-slate-200 hover:border-yellow-300 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    <div class="flex items-center space-x-3">
                      <div class="w-10 h-10 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center group-hover:rotate-6 transition-transform">
                        <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div class="text-left">
                        <p class="text-sm font-semibold text-slate-900 group-hover:text-yellow-700 transition-colors">Rendimiento Lento</p>
                        <p class="text-xs text-slate-500">El sistema va muy lento</p>
                      </div>
                    </div>
                  </button>
                  
                  <button 
                    @click="currentMessage = 'Hay un error en una funcionalidad'; sendMessage();"
                    class="group p-4 bg-white hover:bg-red-50 border border-slate-200 hover:border-red-300 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    <div class="flex items-center space-x-3">
                      <div class="w-10 h-10 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 flex items-center justify-center group-hover:rotate-6 transition-transform">
                        <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div class="text-left">
                        <p class="text-sm font-semibold text-slate-900 group-hover:text-red-700 transition-colors">Error del Sistema</p>
                        <p class="text-xs text-slate-500">Algo no funciona correctamente</p>
                      </div>
                    </div>
                  </button>
                  
                  <button 
                    @click="currentMessage = 'Necesito ayuda general'; sendMessage();"
                    class="group p-4 bg-white hover:bg-green-50 border border-slate-200 hover:border-green-300 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    <div class="flex items-center space-x-3">
                      <div class="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center group-hover:rotate-6 transition-transform">
                        <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div class="text-left">
                        <p class="text-sm font-semibold text-slate-900 group-hover:text-green-700 transition-colors">Ayuda General</p>
                        <p class="text-xs text-slate-500">Otras consultas técnicas</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              <!-- Lista de Mensajes -->
              <ChatMessage 
                v-for="message in messages" 
                :key="message.id"
                :message="message"
                :show-timestamp="true"
              />

              <!-- Indicador de Escritura Premium -->
              <div v-if="isEliasTyping" class="flex items-start space-x-4 animate-fade-in">
                <div class="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg flex-shrink-0">
                  <svg class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <div class="bg-white rounded-3xl rounded-tl-lg px-6 py-4 shadow-lg border border-slate-200/60 max-w-xs">
                  <div class="flex items-center space-x-2">
                    <div class="flex space-x-1">
                      <div class="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style="animation-delay: 0ms;"></div>
                      <div class="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style="animation-delay: 150ms;"></div>
                      <div class="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style="animation-delay: 300ms;"></div>
                    </div>
                    <span class="text-sm text-slate-500 font-medium">Elias está escribiendo...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Input Area Rediseñada -->
          <div class="bg-white border-t border-slate-200/60 p-6 shadow-2xl backdrop-blur-sm">
            <div class="max-w-4xl mx-auto">
              <ChatInput 
                v-model="currentMessage"
                :is-loading="isLoading"
                :disabled="isDisabled"
                @send="sendMessage"
                @quick-action="handleQuickAction"
              />
              
              <!-- Footer informativo -->
              <div class="flex items-center justify-between mt-4 text-xs text-slate-500">
                <div class="flex items-center space-x-4">
                  <span class="flex items-center space-x-1">
                    <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Sesión: {{ sessionId || 'Inicializando...' }}</span>
                  </span>
                  <span class="flex items-center space-x-1">
                    <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>{{ conversationHistory.length }} mensajes</span>
                  </span>
                </div>
                <div class="flex items-center space-x-2">
                  <span class="px-3 py-1 bg-slate-100 text-slate-600 rounded-full font-medium">IA Elias v2.1</span>
                  <span v-if="isEscalated" class="px-3 py-1 bg-orange-100 text-orange-700 rounded-full font-medium">🔄 Escalado</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section v-if="activePanel === 'stats'" class="animate-fade-in-up">
        <div class="mb-6">
          <h2 class="text-2xl font-semibold text-ecomac-primary mb-2">Estadísticas de Soporte</h2>
          <p class="text-gray-500">Análisis detallado del rendimiento del sistema de soporte.</p>
        </div>
        
        <!-- Métricas principales -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          <div class="card items-center text-center hover-lift animate-fade-in-up" style="animation-delay: 0.1s;">
            <div class="p-4 bg-blue-600 text-white rounded-full inline-block mb-4">
              <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <p class="text-3xl font-bold text-gray-900">87%</p>
            <p class="text-gray-500">Satisfacción Cliente</p>
          </div>
          <div class="card items-center text-center hover-lift animate-fade-in-up" style="animation-delay: 0.2s;">
            <div class="p-4 bg-green-600 text-white rounded-full inline-block mb-4">
              <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <p class="text-3xl font-bold text-gray-900">2.3h</p>
            <p class="text-gray-500">Tiempo Promedio</p>
          </div>
          <div class="card items-center text-center hover-lift animate-fade-in-up" style="animation-delay: 0.3s;">
            <div class="p-4 bg-purple-600 text-white rounded-full inline-block mb-4">
              <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
              </svg>
            </div>
            <p class="text-3xl font-bold text-gray-900">+23%</p>
            <p class="text-gray-500">Crecimiento Mensual</p>
          </div>
          <div class="card items-center text-center hover-lift animate-fade-in-up" style="animation-delay: 0.4s;">
            <div class="p-4 bg-orange-600 text-white rounded-full inline-block mb-4">
              <svg class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
            <p class="text-3xl font-bold text-gray-900">156</p>
            <p class="text-gray-500">Usuarios Activos</p>
          </div>
        </div>

        <!-- Gráficos y tablas -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <!-- Gráfico de tendencias -->
          <div class="card animate-fade-in-up" style="animation-delay: 0.5s;">
            <h3 class="text-lg font-semibold text-ecomac-primary mb-4">Tendencia de Tickets (7 días)</h3>
            <div class="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
              <div class="text-center text-gray-500">
                <svg class="h-16 w-16 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
                <p>Gráfico de tendencias</p>
                <p class="text-sm">(En desarrollo)</p>
              </div>
            </div>
          </div>

          <!-- Distribución por prioridad -->
          <div class="card animate-fade-in-up" style="animation-delay: 0.6s;">
            <h3 class="text-lg font-semibold text-ecomac-primary mb-4">Distribución por Prioridad</h3>
            <div class="space-y-4">
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-4 h-4 bg-red-500 rounded"></div>
                  <span class="text-gray-700">Alta</span>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="w-32 bg-gray-200 rounded-full h-2">
                    <div class="bg-red-500 h-2 rounded-full" style="width: 25%"></div>
                  </div>
                  <span class="text-sm text-gray-600">25%</span>
                </div>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span class="text-gray-700">Media</span>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="w-32 bg-gray-200 rounded-full h-2">
                    <div class="bg-yellow-500 h-2 rounded-full" style="width: 45%"></div>
                  </div>
                  <span class="text-sm text-gray-600">45%</span>
                </div>
              </div>
              <div class="flex items-center justify-between">
                <div class="flex items-center space-x-3">
                  <div class="w-4 h-4 bg-green-500 rounded"></div>
                  <span class="text-gray-700">Baja</span>
                </div>
                <div class="flex items-center space-x-2">
                  <div class="w-32 bg-gray-200 rounded-full h-2">
                    <div class="bg-green-500 h-2 rounded-full" style="width: 30%"></div>
                  </div>
                  <span class="text-sm text-gray-600">30%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabla de performance por agente -->
        <div class="card animate-fade-in-up" style="animation-delay: 0.7s;">
          <h3 class="text-lg font-semibold text-ecomac-primary mb-4">Performance por Agente</h3>
          <div class="overflow-x-auto">
            <table class="w-full text-left">
              <thead>
                <tr class="border-b text-gray-500">
                  <th class="py-3 px-2">Agente</th>
                  <th class="py-3 px-2">Tickets Resueltos</th>
                  <th class="py-3 px-2">Tiempo Promedio</th>
                  <th class="py-3 px-2">Satisfacción</th>
                  <th class="py-3 px-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                <tr class="border-b hover:bg-gray-50 transition-colors">
                  <td class="py-4 px-2 font-medium">Juan Pérez</td>
                  <td class="py-4 px-2">42</td>
                  <td class="py-4 px-2">1.8h</td>
                  <td class="py-4 px-2">
                    <span class="px-2 py-1 text-xs rounded bg-green-100 text-green-800">92%</span>
                  </td>
                  <td class="py-4 px-2">
                    <span class="w-2 h-2 bg-green-500 rounded-full inline-block mr-2"></span>
                    Activo
                  </td>
                </tr>
                <tr class="border-b hover:bg-gray-50 transition-colors">
                  <td class="py-4 px-2 font-medium">Ana Silva</td>
                  <td class="py-4 px-2">38</td>
                  <td class="py-4 px-2">2.1h</td>
                  <td class="py-4 px-2">
                    <span class="px-2 py-1 text-xs rounded bg-green-100 text-green-800">89%</span>
                  </td>
                  <td class="py-4 px-2">
                    <span class="w-2 h-2 bg-green-500 rounded-full inline-block mr-2"></span>
                    Activo
                  </td>
                </tr>
                <tr class="hover:bg-gray-50 transition-colors">
                  <td class="py-4 px-2 font-medium">María López</td>
                  <td class="py-4 px-2">35</td>
                  <td class="py-4 px-2">2.5h</td>
                  <td class="py-4 px-2">
                    <span class="px-2 py-1 text-xs rounded bg-yellow-100 text-yellow-800">85%</span>
                  </td>
                  <td class="py-4 px-2">
                    <span class="w-2 h-2 bg-yellow-500 rounded-full inline-block mr-2"></span>
                    Ocupado
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>

    <!-- Modal de Detalle de Tickets -->
    <TicketDetailModal 
      v-model="showTicketDetailModal"
      :ticket="selectedTicket"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../store/authStore';
import { useChatStore } from '../stores/chatStore';
import { getGlobalTicketsStore } from '../stores/useTicketsStore';
import { EcomacLogo } from '../components';
import MetricsHeader from '../components/MetricsHeader.vue';
import ChatMessage from '../components/chat/ChatMessage.vue';
import ChatInput from '../components/chat/ChatInput.vue';
import TypingIndicator from '../components/chat/TypingIndicator.vue';
import EscalationBanner from '../components/chat/EscalationBanner.vue';
import TicketDetailModal from '../components/TicketDetailModal.vue';
// import WebSocketStatus from '../components/WebSocketStatus.vue'; // DESACTIVADO: WebSocket no disponible
import TicketService from '../services/TicketService';
import TicketStatusService from '../services/TicketStatusService.ts';
import { useNotificationService } from '../services/NotificationService';

const router = useRouter();
const authStore = useAuthStore();
const chatStore = useChatStore();
const notificationService = useNotificationService();

// Initialize tickets store
const ticketsStore = getGlobalTicketsStore();

const activePanel = ref('dashboard');

// Mobile menu state
const mobileMenuOpen = ref(false);

// Mobile responsive detection
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);


// Mobile menu functions
function toggleMobileMenu() {
  mobileMenuOpen.value = !mobileMenuOpen.value;
}

function closeMobileMenu() {
  mobileMenuOpen.value = false;
}

// Función de debounce para el resize
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Window resize handler for responsive detection con debounce
const handleResize = debounce(() => {
  const newWidth = window.innerWidth;
  console.log('🔄 Resize detectado:', newWidth);
  
  // Solo actualizar si hay un cambio significativo
  if (Math.abs(newWidth - windowWidth.value) > 10) {
    windowWidth.value = newWidth;
    console.log('✅ Window width actualizado a:', windowWidth.value, 'px');
  }
}, 100); // Debounce de 100ms


// Setup window resize listener
if (typeof window !== 'undefined') {
  window.addEventListener('resize', handleResize);
}

// Ticket detail modal state
const showTicketDetailModal = ref(false);
const selectedTicket = ref(null);

// Computed properties from tickets store
const ticketsList = computed(() => ticketsStore.filteredTickets.value);
const isLoadingTickets = ticketsStore.isLoading; // Ya es un computed, no necesita .value ni nuevo computed
const ticketsError = computed(() => ticketsStore.state.error);

// Filtros y búsqueda
const searchQuery = ref('');
const filterStatus = ref('');
const filterPriority = ref('');
const viewMode = ref('table'); // 'table' or 'cards' - Cambiar default a tabla
const showFilters = ref(false); // Control de visibilidad de filtros en móviles/tablets

// Paginación
const currentPage = ref(1);
const itemsPerPage = ref(3);

// Paginación para dashboard
const dashboardCurrentPage = ref(1);
const dashboardItemsPerPage = ref(3);

// Chat refs
const messagesContainer = ref(null);
const currentMessage = ref('');
const isLoading = ref(false);
const isEliasTyping = ref(false);
const isConnected = ref(true);
const responseTime = ref(null);
const connectionQuality = ref('excellent'); // 'excellent', 'good', 'poor', 'offline'
const lastMessageTimestamp = ref(null);

// Nuevas refs para indicadores avanzados
const sessionStartTime = ref(null);
const escalationTime = ref(null);
const isUserTyping = ref(false);
const userTypingTimeout = ref(null);
const maxMessageLength = ref(2000);
const predictions = ref([]);
const messageStatuses = ref(new Map()); // Para rastrear estados de mensajes

// Chat computed
const messages = computed(() => chatStore.messages || []);
const sessionId = computed(() => chatStore.sessionId || null);
const conversationHistory = computed(() => chatStore.conversationHistory || []);
const isEscalated = computed(() => chatStore.isEscalated || false);
const isDisabled = computed(() => isLoading.value || !isConnected.value);
const connectionStatus = computed(() => isConnected.value ? 'Conectado' : 'Desconectado');

// Propiedades para el banner de escalamiento (simuladas para demo)
const assignedAgent = computed(() => {
  if (!isEscalated.value) return null;
  return {
    name: 'María González',
    id: 'maria.gonzalez',
    avatar: null // Se puede agregar URL del avatar del agente
  };
});

const escalationPriority = computed(() => {
  if (!isEscalated.value) return null;
  return 'alta'; // 'alta', 'media', 'baja'
});

const escalationTicketId = computed(() => {
  if (!isEscalated.value) return null;
  return 'TK-2024-001';
});

const estimatedResponseTime = computed(() => {
  if (!isEscalated.value) return null;
  return '2 horas'; // Tiempo estimado de respuesta
});

// Computed para duración de sesión
const sessionDuration = computed(() => {
  if (!sessionStartTime.value) {
    sessionStartTime.value = new Date();
    return '00:00';
  }
  
  const now = new Date();
  const diff = now - sessionStartTime.value;
  const minutes = Math.floor(diff / 60000);
  const seconds = Math.floor((diff % 60000) / 1000);
  
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
});

const showEscalationWarning = computed(() => {
  const history = conversationHistory.value || [];
  return history.length > 2 && !isEscalated.value;
});

const showQuickActions = computed(() => {
  const msgs = messages.value || [];
  return msgs.length === 0 || (msgs.length > 0 && !isLoading.value);
});

// Quick actions
const quickActions = ref([]);

// Estado de carga del dashboard
const isDashboardLoading = ref(false);

// Avatar state
const avatarLoaded = ref(true);

// Estado para estabilización durante carga de datos desde webhook
const isDataStabilizing = ref(false);
const dataStabilizationTimeout = ref(null);

// Computed para detección de vista móvil (ahora reactivo)
const isMobileView = computed(() => {
  // En dispositivos móviles/tablet (< 1024px), forzar vista de cards
  return windowWidth.value < 1024; // lg breakpoint de Tailwind
});

// Chat connection status computed properties
const connectionIndicatorClass = computed(() => {
  if (!isConnected.value) {
    return 'bg-gray-400 animate-none';
  }
  
  switch (connectionQuality.value) {
    case 'excellent':
      return 'bg-green-500 animate-pulse shadow-lg shadow-green-500/50';
    case 'good':
      return 'bg-yellow-500 animate-pulse shadow-md shadow-yellow-500/50';
    case 'poor':
      return 'bg-orange-500 animate-pulse shadow-md shadow-orange-500/50';
    case 'offline':
      return 'bg-red-500 animate-none shadow-md shadow-red-500/50';
    default:
      return 'bg-green-500 animate-pulse shadow-lg shadow-green-500/50';
  }
});

const connectionTextClass = computed(() => {
  if (!isConnected.value) {
    return 'text-gray-600';
  }
  
  switch (connectionQuality.value) {
    case 'excellent':
      return 'text-green-700';
    case 'good':
      return 'text-yellow-700';
    case 'poor':
      return 'text-orange-700';
    case 'offline':
      return 'text-red-700';
    default:
      return 'text-green-700';
  }
});

// Computed properties para estadísticas usando el store
const ticketStats = computed(() => {
  // Verificar que el store esté inicializado
  if (!ticketsStore || !ticketsStore.getAllTickets) {
    return {
      total: 0,
      abierto: 0,
      en_progreso: 0,
      resuelto: 0,
      cerrado: 0
    };
  }
  
  // Acceder a getAllTickets, que ahora es un computed directo
  const allTickets = ticketsStore.getAllTickets?.value || [];
  
  
  // Contar manualmente por estados para asegurar precisión
  const stats = {
    total: allTickets.length,
    abierto: 0,
    en_progreso: 0,
    resuelto: 0,
    cerrado: 0
  };
  
  allTickets.forEach(ticket => {
    const status = ticket.status ? ticket.status.toLowerCase() : '';
    
    switch (status) {
      case 'open':
      case 'abierto':
      case 'pending':
        stats.abierto++;
        break;
      case 'in_progress':
      case 'en_progreso':
      case 'in progress':
        stats.en_progreso++;
        break;
      case 'resolved':
      case 'resuelto':
      case 'complete':
      case 'done':
        stats.resuelto++;
        break;
      case 'closed':
      case 'cerrado':
        stats.cerrado++;
        break;
      default:
        console.warn('Estado de ticket no reconocido:', status, 'para ticket:', ticket.id);
        // Por defecto, contar como abierto si no reconocemos el estado
        stats.abierto++;
        break;
    }
  });
  
  
  return stats;
});

// Funciones auxiliares para fechas
function getStartOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is sunday
  const monday = new Date(d.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
}

function getEndOfWeek(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1) + 6; // Sunday of current week
  const sunday = new Date(d.setDate(diff));
  sunday.setHours(23, 59, 59, 999);
  return sunday;
}

// Nuevas métricas más comprensibles para el dashboard
const dashboardStats = computed(() => {
  const allTickets = ticketsStore?.getAllTickets?.value || [];
  
  // Agrupar por categorías más comprensibles
  const stats = {
    activos: 0,      // Tickets que requieren atención (abierto + evaluación)
    enProceso: 0,    // Tickets en desarrollo activo
    completados: 0,  // Tickets finalizados (resuelto + cerrado)
    urgentes: 0,     // Tickets de prioridad alta
    semanaActual: 0  // Tickets de esta semana
  };
  
  const now = new Date();
  const startOfWeek = getStartOfWeek();
  const endOfWeek = getEndOfWeek();
  
  allTickets.forEach(ticket => {
    const status = ticket.status ? ticket.status.toLowerCase() : '';
    const priority = ticket.priority ? ticket.priority.toLowerCase() : '';
    const createdDate = ticket.createdAt ? new Date(ticket.createdAt) : null;
    
    // Categorizar por estados agrupados
    if (['open', 'abierto', 'pending', 'pendientes', 'evaluacion', 'evaluación'].includes(status)) {
      stats.activos++;
    } else if (['in_progress', 'en_progreso', 'en desarrollo', 'en_desarrollo', 
               'pendiente_dev', 'pendiente dev', 'testeo_local', 'testeo local',
               'pendiente_servidor_dev', 'testeo_servidor_dev',
               'pendiente_a_qa', 'testeo_servidor_qa'].includes(status)) {
      stats.enProceso++;
    } else if (['resolved', 'resuelto', 'complete', 'done', 'closed', 'cerrado', 
               'listo', 'bugs_corregidos', 'bugs corregidos'].includes(status)) {
      stats.completados++;
    }
    
    // Contar urgentes por prioridad
    if (['high', 'alta', 'urgent', 'urgente'].includes(priority)) {
      stats.urgentes++;
    }
    
    // Contar tickets de la semana actual
    if (createdDate && createdDate >= startOfWeek && createdDate <= endOfWeek) {
      stats.semanaActual++;
    }
  });
  
  return stats;
});


function isTicketInCurrentWeek(ticket) {
  if (!ticket || !ticket.createdAt) return false;
  
  const ticketDate = new Date(ticket.createdAt);
  const startOfWeek = getStartOfWeek();
  const endOfWeek = getEndOfWeek();
  
  return ticketDate >= startOfWeek && ticketDate <= endOfWeek;
}

function isTicketActive(ticket) {
  if (!ticket || !ticket.status) return false;
  
  // Expandir la lista de estados activos para incluir todos los estados del workflow de Ecomac
  const activeStatuses = [
    // Estados básicos
    'open', 'abierto', 'pending', 'pendiente', 'pendientes',
    'in_progress', 'en_progreso', 'in progress',
    
    // Estados del workflow completo de Ecomac
    'evaluacion', 'evaluación',
    'pendiente_dev', 'pendiente dev',
    'en_desarrollo', 'en desarrollo', 'development',
    'testeo_local', 'testeo local',
    'pendiente_servidor_dev', 'pendiente servidor dev',
    'testeo_servidor_dev', 'testeo servidor dev',
    'pendiente_a_qa', 'pendiente a qa',
    'testeo_servidor_qa', 'testeo servidor qa',
    'bugs_encontrados', 'bugs encontrados'
  ];
  
  const status = ticket.status.toLowerCase();
  return activeStatuses.includes(status);
}

// Computed property para el rango de la semana actual
const currentWeekRange = computed(() => {
  const startOfWeek = getStartOfWeek();
  const endOfWeek = getEndOfWeek();
  
  const formatOptions = { 
    day: '2-digit', 
    month: '2-digit' 
  };
  
  const start = startOfWeek.toLocaleDateString('es-ES', formatOptions);
  const end = endOfWeek.toLocaleDateString('es-ES', formatOptions);
  
  return `${start} - ${end}`;
});

// Computed property para todos los tickets recientes (activos, priorizando los más recientes)
const allRecentTickets = computed(() => {
  const allTickets = ticketsStore.filteredTickets.value || [];
  const startOfWeek = getStartOfWeek();
  const endOfWeek = getEndOfWeek();
  
console.log('🎯 [DEBUG] Calculando tickets recientes:', {
    totalTickets: allTickets.length,
    currentWeekRange: currentWeekRange.value,
    currentYear: new Date().getFullYear(),
    currentDate: new Date().toISOString(),
    startOfWeek: startOfWeek.toISOString(),
    endOfWeek: endOfWeek.toISOString(),
    allTicketsIds: allTickets.map(t => ({
      id: t.id,
      title: t.title?.substring(0, 30) + '...',
      status: t.status,
      statusRaw: t.statusRaw || 'N/A',
      createdAt: t.createdAt,
      createdYear: t.createdAt ? new Date(t.createdAt).getFullYear() : 'N/A',
      isInFuture: t.createdAt ? new Date(t.createdAt) > new Date() : false
    }))
  });
  
  // DETECTAR PROBLEMA DE AÑO FUTURO
  const futureTickets = allTickets.filter(t => {
    if (!t.createdAt) return false;
    const ticketDate = new Date(t.createdAt);
    const currentDate = new Date();
    return ticketDate.getFullYear() > currentDate.getFullYear();
  });
  
  if (futureTickets.length > 0) {
    console.warn('⚠️ [WEBHOOK ISSUE] Detectados tickets con fechas futuras (posible problema de timestamp del webhook):', {
      futureTicketsCount: futureTickets.length,
      currentYear: new Date().getFullYear(),
      futureTicketsDetails: futureTickets.map(t => ({
        id: t.id,
        title: t.title?.substring(0, 50) + '...',
        createdAt: t.createdAt,
        year: new Date(t.createdAt).getFullYear(),
        expectedYear: new Date().getFullYear()
      }))
    });
  }
  
  // NUEVA LÓGICA: Mostrar tickets activos priorizando los de la semana actual
  const activeTickets = allTickets.filter(ticket => {
    const isActive = isTicketActive(ticket);
    const isCurrentWeek = isTicketInCurrentWeek(ticket);
    
    console.log('🔍 [DEBUG] Evaluando ticket:', {
      id: ticket.id,
      title: ticket.title?.substring(0, 50) + '...',
      status: ticket.status,
      createdAt: ticket.createdAt,
      createdDate: ticket.createdAt ? new Date(ticket.createdAt).toISOString() : null,
      isActive,
      isCurrentWeek,
      activeStatusMatch: ticket.status ? [
        'open', 'abierto', 'pending', 'pendiente', 'pendientes',
        'in_progress', 'en_progreso', 'in progress',
        'evaluacion', 'evaluación',
        'pendiente_dev', 'pendiente dev',
        'en_desarrollo', 'en desarrollo', 'development',
        'testeo_local', 'testeo local',
        'pendiente_servidor_dev', 'pendiente servidor dev',
        'testeo_servidor_dev', 'testeo servidor dev',
        'pendiente_a_qa', 'pendiente a qa',
        'testeo_servidor_qa', 'testeo servidor qa',
        'bugs_encontrados', 'bugs encontrados'
      ].includes(ticket.status.toLowerCase()) : false
    });
    
    return isActive;
  });
  
  // Ordenar por fecha de creación (más recientes primero)
  const sortedTickets = activeTickets.sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateB - dateA; // Orden descendente (más recientes primero)
  });
  
  // ANÁLISIS DETALLADO DE FILTRADO POR FECHAS
  console.log('📅 [DEBUG FECHAS] Análisis detallado de filtrado temporal:', {
    currentSystemDate: new Date().toISOString(),
    currentSystemYear: new Date().getFullYear(),
    startOfWeekFilter: startOfWeek.toISOString(),
    endOfWeekFilter: endOfWeek.toISOString(),
    weekRange: currentWeekRange.value,
    
    ticketsWithDates: allTickets.map(t => ({
      id: t.id,
      title: t.title?.substring(0, 40) + '...',
      status: t.status,
      statusRaw: t.statusRaw,
      createdAt: t.createdAt,
      createdDate_parsed: t.createdAt ? new Date(t.createdAt).toISOString() : null,
      createdYear: t.createdAt ? new Date(t.createdAt).getFullYear() : null,
      createdMonth: t.createdAt ? new Date(t.createdAt).getMonth() + 1 : null,
      createdDay: t.createdAt ? new Date(t.createdAt).getDate() : null,
      isInCurrentWeek: isTicketInCurrentWeek(t),
      isInCurrentYear: t.createdAt ? new Date(t.createdAt).getFullYear() === new Date().getFullYear() : false,
      isInFuture: t.createdAt ? new Date(t.createdAt) > new Date() : false,
      daysDifference: t.createdAt ? Math.round((new Date(t.createdAt) - new Date()) / (1000 * 60 * 60 * 24)) : null,
      isActive: isTicketActive(t)
    }))
  });
  
  console.log('✅ [DEBUG] Tickets recientes calculados:', {
    activeTickets: activeTickets.length,
    sortedTickets: sortedTickets.length,
    ticketsThisWeek: sortedTickets.filter(t => isTicketInCurrentWeek(t)).length,
    tickets: sortedTickets.slice(0, 10).map(t => ({
      id: t.id,
      title: t.title?.substring(0, 30) + '...',
      status: t.status,
      createdAt: t.createdAt,
      isCurrentWeek: isTicketInCurrentWeek(t),
      isActive: isTicketActive(t)
    }))
  });
  
  return sortedTickets;
});

// Computed para paginación del dashboard
const dashboardTotalPages = computed(() => {
  return Math.ceil(allRecentTickets.value.length / dashboardItemsPerPage.value);
});

// Computed property para tickets recientes paginados para el dashboard
const recentTickets = computed(() => {
  const start = (dashboardCurrentPage.value - 1) * dashboardItemsPerPage.value;
  const end = start + dashboardItemsPerPage.value;
  return allRecentTickets.value.slice(start, end);
});

const dashboardPaginationInfo = computed(() => {
  const start = (dashboardCurrentPage.value - 1) * dashboardItemsPerPage.value + 1;
  const end = Math.min(dashboardCurrentPage.value * dashboardItemsPerPage.value, allRecentTickets.value.length);
  return {
    start,
    end,
    total: allRecentTickets.value.length
  };
});

// Lista filtrada de tickets para la vista de tickets (TODOS los tickets, no solo los recientes)
const allFilteredTickets = computed(() => {
  // Solo procesar si estamos en el panel de tickets
  if (activePanel.value !== 'tickets') {
    return [];
  }
  
  // Actualizar filtros del store cuando cambien los filtros locales
  ticketsStore.updateFilters({
    search: searchQuery.value,
    status: filterStatus.value ? [filterStatus.value] : [],
    priority: filterPriority.value ? [filterPriority.value] : []
  });
  
  // Obtener TODOS los tickets filtrados y ordenarlos por fecha de creación (más recientes primero)
  const tickets = ticketsStore.filteredTickets.value || [];
  return [...tickets].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateB - dateA; // Orden descendente (más recientes primero)
  });
});

// Computed para la paginación
const totalPages = computed(() => {
  return Math.ceil(allFilteredTickets.value.length / itemsPerPage.value);
});

const filteredTickets = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return allFilteredTickets.value.slice(start, end);
});

const paginationInfo = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value + 1;
  const end = Math.min(currentPage.value * itemsPerPage.value, allFilteredTickets.value.length);
  return {
    start,
    end,
    total: allFilteredTickets.value.length
  };
});

// Panel functions
function setActivePanel(panel) {
  activePanel.value = panel;
  
  // Inicializar chat cuando se activa el panel
  if (panel === 'chat') {
    initializeChat();
  }
  
  // Solo cargar tickets si no hay tickets o si hace más de 5 minutos de la última carga
  if (panel === 'tickets') {
    const hasTickets = ticketsList.value.length > 0;
    const lastSync = ticketsStore.state.lastSync;
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
    
    // Cargar solo si no hay tickets O si la última sincronización fue hace más de 5 minutos
    // IMPORTANTE: Esta carga es silenciosa (sin notificaciones) porque es automática
    if (!hasTickets || !lastSync || lastSync < fiveMinutesAgo) {
      console.log('🔄 Carga automática de tickets al cambiar panel:', {
        noTickets: !hasTickets,
        noLastSync: !lastSync,
        syncTooOld: lastSync < fiveMinutesAgo,
        lastSync
      });
      loadUserTicketsSilently(); // Carga silenciosa sin notificaciones
    } else {
      console.log('⏭️ Tickets ya cargados y actualizados, no es necesario recargar');
    }
  }
}

function viewTicket(ticketId) {
  // Buscar el ticket en la lista actual de tickets
  const ticket = ticketsList.value.find(t => t.id === ticketId || t.id === String(ticketId));
  if (ticket) {
    // CRÍTICO: Seleccionar el ticket con copia profunda para evitar mutaciones
    selectedTicket.value = {
      ...JSON.parse(JSON.stringify(ticket)), // Copia profunda completa
      assignedTo: String(ticket.assignedTo || 'Elias AI') // Asegurar nueva string
    };
    showTicketDetailModal.value = true;
  } else {
    console.warn('Ticket no encontrado:', ticketId);
  }
}

async function logout() {
  try {
    await authStore.logout();
    router.push('/login');
  } catch (error) {
    console.error('Error during logout:', error);
    // Redirect anyway for better UX
    router.push('/login');
  }
}

// Chat functions
async function initializeChat() {
  // Inicializar sesión si no existe
  if (!sessionId.value) {
    await chatStore.startNewSession();
  }
  
  // Agregar mensaje de bienvenida si no hay mensajes
  if (messages.value.length === 0) {
    setTimeout(async () => {
      await chatStore.addEliasMessage(
        '¡Hola! Soy Elias, tu asistente de IA. Estoy aquí para ayudarte con cualquier problema técnico que tengas. ' +
        '¿En qué puedo ayudarte hoy? 😊'
      );
    }, 1000);
  }
}

async function sendMessage() {
  if (!currentMessage.value.trim() || isLoading.value) return;

  const messageText = currentMessage.value.trim();
  currentMessage.value = '';
  isLoading.value = true;

  let messageId = null;

  try {
    // Agregar mensaje del usuario con estado 'sending'
    messageId = await chatStore.addUserMessage(messageText);
    
    // Scroll hacia abajo
    scrollToBottom();
    
    // Simular entrega del mensaje
    setTimeout(() => {
      if (messageId) {
        chatStore.updateMessageStatus(messageId, 'delivered');
      }
    }, 300);
    
    // Simular que Elias está escribiendo
    isEliasTyping.value = true;
    
    // Marcar mensaje como leído cuando Elias comience a responder
    setTimeout(() => {
      if (messageId) {
        chatStore.updateMessageStatus(messageId, 'read');
      }
    }, 800);
    
    // Enviar mensaje a Elias AI
    const response = await chatStore.sendMessageToElias(messageText);
    
    // Simular delay de respuesta
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    isEliasTyping.value = false;
    
    scrollToBottom();
    
    // Restaurar el foco al textarea después de la respuesta
    nextTick(() => {
      const chatInput = document.querySelector('textarea[placeholder="Describe tu problema aquí..."]');
      if (chatInput) {
        chatInput.focus();
      }
    });
    
  } catch (error) {
    console.error('Error sending message:', error);
    
    // Si hay error, marcar el mensaje como no entregado
    if (messageId) {
      chatStore.updateMessageStatus(messageId, 'error');
    }
    
    await chatStore.addSystemMessage('Error al enviar mensaje. Por favor, intenta de nuevo.');
  } finally {
    isLoading.value = false;
  }
}

async function handleQuickAction(action) {
  const quickMessages = {
    login: 'Tengo problemas para iniciar sesión en el sistema',
    performance: 'El sistema está funcionando muy lento',
    error: 'Hay un error en una funcionalidad específica',
    integration: 'Tengo problemas con una integración',
    other: 'Tengo otro tipo de problema técnico'
  };
  
  currentMessage.value = quickMessages[action.id] || action.label;
  await sendMessage();
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
    }
  });
}

async function clearChat() {
  await chatStore.clearChat();
  currentMessage.value = '';
}

async function startNewSession() {
  await chatStore.startNewSession();
  currentMessage.value = '';
}

// Ticket modal functions
function handleTicketSaved(updatedTicket) {
  // Notificar al store que actualice el ticket
  ticketsStore.updateTicket(updatedTicket);
}

function handleTicketCancelled() {
  // El modal ya se cierra automáticamente, no necesitamos hacer nada más
}

// Load user tickets CON notificaciones (para botones de refresh)
async function loadUserTickets() {
  // Verificar autenticación primero
  if (!authStore.isAuthenticated) {
    console.warn('Usuario no autenticado, intentando auto-login...');
    const loginSuccess = await authStore.autoLogin();
    if (!loginSuccess) {
      console.error('Usuario no autenticado. Por favor, inicia sesión nuevamente.');
      return;
    }
  }
  
  // Obtener el email del usuario autenticado
  const userEmail = authStore.user?.email || authStore.user?.username || authStore.userEmail;
  
  if (!userEmail) {
    console.error('Email no disponible para cargar tickets');
    return;
  }
  
  // Activar estado de carga del dashboard
  isDashboardLoading.value = true;
  
  // NUEVO: Estabilizar diseño durante carga de datos
  stabilizeDesignDuringDataLoad();
  
  try {
    // IMPORTANTE: Esta función NO usa notificaciones del store porque tenemos notificaciones personalizadas
    console.log('🔔 Carga MANUAL de tickets (SIN notificaciones del store, usa notificaciones personalizadas)');
    await ticketsStore.loadTickets(userEmail, { showNotifications: false });
    
    // NUEVO: Notificar sobre los tickets cargados según el panel activo
    if (activePanel.value === 'dashboard') {
      checkAndNotifyRecentTickets();
    } else if (activePanel.value === 'tickets') {
      checkAndNotifyAllTickets();
    }
    
  } catch (error) {
    console.error('Error al cargar tickets:', error);
  } finally {
    // Solo desactivar estado de carga del dashboard
    // El estado de loading del store se maneja automáticamente en loadTickets()
    isDashboardLoading.value = false;
    
    // NUEVO: Finalizar estabilización del diseño
    finishDesignStabilization();
  }
}

// Load user tickets SIN notificaciones (para cambios automáticos de panel)
async function loadUserTicketsSilently() {
  // Verificar autenticación primero
  if (!authStore.isAuthenticated) {
    console.warn('Usuario no autenticado, intentando auto-login...');
    const loginSuccess = await authStore.autoLogin();
    if (!loginSuccess) {
      console.error('Usuario no autenticado. Por favor, inicia sesión nuevamente.');
      return;
    }
  }
  
  // Obtener el email del usuario autenticado
  const userEmail = authStore.user?.email || authStore.user?.username || authStore.userEmail;
  
  if (!userEmail) {
    console.error('Email no disponible para cargar tickets');
    return;
  }
  
  // NUEVO: Estabilizar diseño durante carga silenciosa
  stabilizeDesignDuringDataLoad();
  
  try {
    // IMPORTANTE: Esta función NO muestra notificaciones porque es automática
    await ticketsStore.loadTickets(userEmail, { showNotifications: false });
    
    
  } catch (error) {
    console.error('Error al cargar tickets silenciosamente:', error);
  } finally {
    // NUEVO: Finalizar estabilización del diseño
    finishDesignStabilization();
  }
}

// Función para verificar y notificar sobre tickets recientes después de actualización manual
function checkAndNotifyRecentTickets() {
  const totalTickets = ticketStats.value.total;
  const recentTicketsCount = allRecentTickets.value.length;
  
  
  if (totalTickets > 0 && recentTicketsCount === 0) {
    // Hay tickets en total, pero ninguno reciente en la semana cursante
    
    // Usar el NotificationService
    notificationService.info(
      `Tickets actualizados correctamente. No hay tickets activos para la semana del ${currentWeekRange.value}.`
    );
    
  } else if (totalTickets === 0) {
    // No hay tickets en absoluto
    
    notificationService.info(
      'Tickets actualizados correctamente. No tienes tickets de soporte creados.'
    );
    
  } else if (recentTicketsCount > 0) {
    // Hay tickets recientes, notificación positiva
    
    notificationService.success(
      `Tickets actualizados correctamente. ${recentTicketsCount} ticket${recentTicketsCount > 1 ? 's' : ''} activo${recentTicketsCount > 1 ? 's' : ''} en la semana del ${currentWeekRange.value}.`
    );
  }
}

// Función para verificar y notificar sobre todos los tickets después de actualización manual en panel tickets
function checkAndNotifyAllTickets() {
  const totalTickets = ticketStats.value.total;
  const allFilteredCount = allFilteredTickets.value.length;
  
  
  if (totalTickets === 0) {
    // No hay tickets en absoluto
    
    notificationService.info(
      'Tickets actualizados correctamente. No tienes tickets de soporte creados.'
    );
    
  } else if (allFilteredCount === 0 && (searchQuery.value || filterStatus.value || filterPriority.value)) {
    // Hay tickets pero ninguno coincide con los filtros
    
    notificationService.warning(
      `Tickets actualizados correctamente. No hay tickets que coincidan con los filtros aplicados (${totalTickets} tickets en total).`
    );
    
  } else if (allFilteredCount > 0) {
    // Hay tickets que coinciden con los filtros
    
    const hasFilters = searchQuery.value || filterStatus.value || filterPriority.value;
    const message = hasFilters 
      ? `Tickets actualizados correctamente. ${allFilteredCount} ticket${allFilteredCount > 1 ? 's' : ''} encontrado${allFilteredCount > 1 ? 's' : ''} (${totalTickets} en total).`
      : `Tickets actualizados correctamente. ${totalTickets} ticket${totalTickets > 1 ? 's' : ''} disponible${totalTickets > 1 ? 's' : ''}.`;
    
    notificationService.success(message);
    
  } else {
    // Caso por defecto
    
    notificationService.success(
      `Tickets actualizados correctamente. ${totalTickets} ticket${totalTickets > 1 ? 's' : ''} disponible${totalTickets > 1 ? 's' : ''}.`
    );
  }
}

async function forceReloadEverything() {
  try {
    // 1. Limpiar completamente el store de tickets
    ticketsStore.clear();
    
    // 2. Forzar actualización del estado de autenticación
    if (!authStore.isAuthenticated) {
      await authStore.autoLogin();
    }
    
    // 3. Limpiar filtros
    searchQuery.value = '';
    filterStatus.value = '';
    filterPriority.value = '';
    
    // 4. Recargar tickets
    await loadUserTickets();
    
    // 5. Si estamos en el panel de chat, reiniciar sesión
    if (activePanel.value === 'chat') {
      await chatStore.startNewSession();
    }
  } catch (error) {
    console.error('❌ Error durante la recarga forzada:', error);
  }
}

// Edit ticket function
function editTicket(ticket) {
  // CRÍTICO: Crear copia profunda para evitar mutaciones compartidas
  selectedTicket.value = {
    ...JSON.parse(JSON.stringify(ticket)),
    assignedTo: String(ticket.assignedTo || 'Elias AI')
  };
  
  showTicketDetailModal.value = true;
}

// Helper functions for ticket display
function formatStatusText(status) {
  // Usar TicketStatusService para obtener el displayName correcto
  const statusData = TicketStatusService.formatStatusForDisplay(status, false);
  if (statusData && statusData !== status) {
    return statusData;
  }
  
  // Fallback manual para estados que no están en el servicio
  const statusMap = {
    // Estados del workflow completo
    'PENDIENTES': 'Pendiente',
    'EVALUACION': 'En análisis',
    'EVALUACIÓN': 'En análisis',
    'PENDIENTE_DEV': 'Derivado a Desarrollo',
    'PENDIENTE DEV': 'Derivado a Desarrollo',
    'EN_DESARROLLO': 'En proceso',
    'EN DESARROLLO': 'En proceso',
    'TESTEO_LOCAL': 'Testeo en desarrollo',
    'TESTEO LOCAL': 'Testeo en desarrollo',
    'PENDIENTE_SERVIDOR_DEV': 'Implementando en desarrollo',
    'PENDIENTE SERVIDOR DEV': 'Implementando en desarrollo',
    'TESTEO_SERVIDOR_DEV': 'Testeando en desarrollo',
    'TESTEO SERVIDOR DEV': 'Testeando en desarrollo',
    'PENDIENTE_A_QA': 'Enviado a QA',
    'PENDIENTE A QA': 'Enviado a QA',
    'TESTEO_SERVIDOR_QA': 'Testeando en QA',
    'TESTEO SERVIDOR QA': 'Testeando en QA',
    'BUGS_ENCONTRADOS': 'Bug encontrado',
    'BUGS ENCONTRADOS': 'Bug encontrado',
    'BUGS_CORREGIDOS': 'Bug Corregido',
    'BUGS CORREGIDOS': 'Bug Corregido',
    'LISTO': 'Cerrado',
    
    // Estados simplificados (compatibilidad)
    'abierto': 'Pendiente',
    'en_progreso': 'En proceso',
    'en progreso': 'En proceso',
    'resuelto': 'Bug Corregido',
    'cerrado': 'Cerrado',
    
    // Estados en inglés (compatibilidad)
    'open': 'Pendiente',
    'pending': 'Pendiente',
    'in_progress': 'En proceso',
    'in progress': 'En proceso',
    'development': 'En proceso',
    'testing': 'Testeo en desarrollo',
    'qa': 'Enviado a QA',
    'resolved': 'Bug Corregido',
    'completed': 'Bug Corregido',
    'complete': 'Bug Corregido',
    'done': 'Bug Corregido',
    'closed': 'Cerrado',
    'finished': 'Cerrado'
  };
  
  const normalizedStatus = status?.toString().toUpperCase();
  return statusMap[normalizedStatus] || statusMap[status] || status;
}

function formatPriorityText(priority) {
  const priorityMap = {
    // Prioridades en español (portal cliente)
    'alta': 'Alta',
    'media': 'Media',
    'baja': 'Baja',
    // Prioridades en inglés (compatibilidad)
    'low': 'Baja',
    'medium': 'Media',
    'high': 'Alta',
    'urgent': 'Urgente'
  };
  return priorityMap[priority] || priority;
}

// Helper functions para la vista en cards
function getTicketInitials(ticket) {
  if (!ticket.title) return '#' + (ticket.id || '?');
  
  // Opción 1: Usar ID del ticket si está disponible
  if (ticket.id) {
    return '#' + ticket.id;
  }
  
  // Opción 2: Si no hay ID, usar iniciales mejoradas
  const title = ticket.title.trim();
  const words = title.split(' ').filter(word => word.length > 0);
  
  if (words.length >= 2) {
    // Tomar la primera letra de las dos primeras palabras significativas
    return (words[0][0] + words[1][0]).toUpperCase();
  } else if (words.length === 1) {
    // Si solo hay una palabra, tomar las dos primeras letras
    const word = words[0];
    if (word.length >= 2) {
      return word.substring(0, 2).toUpperCase();
    } else {
      return word.toUpperCase() + 'T';
    }
  }
  
  // Fallback
  return 'TK';
}

// Función para obtener el tag principal del ticket
function getTicketMainTag(ticket) {
  // Si el ticket tiene tags, usar el primero como principal
  if (ticket.tags && Array.isArray(ticket.tags) && ticket.tags.length > 0) {
    return ticket.tags[0];
  }
  
  // Si no hay tags, determinar el tipo basado en el título o descripción
  const title = (ticket.title || '').toLowerCase();
  const description = (ticket.description || '').toLowerCase();
  
  // Palabras clave para clasificar el tipo de ticket
  if (title.includes('bug') || title.includes('error') || title.includes('fallo') || 
      description.includes('bug') || description.includes('error') || description.includes('fallo')) {
    return 'Bug';
  }
  
  if (title.includes('mejora') || title.includes('optimizar') || title.includes('actualizar') ||
      description.includes('mejora') || description.includes('optimizar') || description.includes('actualizar')) {
    return 'Mejora';
  }
  
  if (title.includes('nueva') || title.includes('implementar') || title.includes('agregar') ||
      description.includes('nueva') || description.includes('implementar') || description.includes('agregar')) {
    return 'Feature';
  }
  
  if (title.includes('soporte') || title.includes('ayuda') || title.includes('consulta') ||
      description.includes('soporte') || description.includes('ayuda') || description.includes('consulta')) {
    return 'Soporte';
  }
  
  // Por defecto, clasificar como incidencia
  return 'Incidencia';
}

// Helper function para obtener iniciales del asignado
function getAssigneeInitials(assignedTo) {
  if (!assignedTo || assignedTo === 'Sin asignar') {
    return 'SA'; // Sin Asignar
  }
  
  // Casos especiales
  if (assignedTo === 'Elias AI') {
    return 'EA';
  }
  
  // Obtener iniciales del nombre
  const name = assignedTo.trim();
  const words = name.split(' ').filter(word => word.length > 0);
  
  if (words.length >= 2) {
    // Tomar primera letra del primer y último nombre
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  } else if (words.length === 1) {
    // Si solo hay una palabra, tomar las dos primeras letras
    const word = words[0];
    if (word.length >= 2) {
      return word.substring(0, 2).toUpperCase();
    } else {
      return word.toUpperCase() + 'A';
    }
  }
  
  // Fallback
  return 'AS'; // ASigned
}

function getStatusIndicatorClass(status) {
  const statusClasses = {
    'open': 'bg-red-500',
    'abierto': 'bg-red-500',
    'pending': 'bg-red-500',
    'in_progress': 'bg-yellow-500',
    'en_progreso': 'bg-yellow-500',
    'resolved': 'bg-green-500',
    'resuelto': 'bg-green-500',
    'closed': 'bg-gray-500',
    'cerrado': 'bg-gray-500'
  };
  return statusClasses[status?.toLowerCase()] || 'bg-gray-400';
}

function getPriorityGradientClass(priority) {
  const priorityGradients = {
    'high': 'bg-gradient-to-r from-red-500 to-red-600',
    'alta': 'bg-gradient-to-r from-red-500 to-red-600',
    'urgent': 'bg-gradient-to-r from-red-600 to-red-700',
    'medium': 'bg-gradient-to-r from-yellow-500 to-yellow-600',
    'media': 'bg-gradient-to-r from-yellow-500 to-yellow-600',
    'low': 'bg-gradient-to-r from-green-500 to-green-600',
    'baja': 'bg-gradient-to-r from-green-500 to-green-600'
  };
  return priorityGradients[priority?.toLowerCase()] || 'bg-gradient-to-r from-gray-400 to-gray-500';
}

function getProgressPercentage(ticket) {
  
  // OPCIÓN 1: Si el webhook ya procesó el progressPercent, usarlo directamente
  if (typeof ticket === 'object' && typeof ticket.progressPercent === 'number') {
    const percentage = Math.round(ticket.progressPercent);
    return percentage;
  }
  
  // OPCIÓN 2: Si recibimos el ticket completo, intentamos obtener el progreso real de ClickUp
  if (typeof ticket === 'object' && ticket.custom_fields && Array.isArray(ticket.custom_fields) && ticket.custom_fields.length > 0) {
    const progressField = ticket.custom_fields.find(field => 
      field.name === 'Progreso' && field.type === 'automatic_progress'
    );
    if (progressField && progressField.value && typeof progressField.value.percent_complete === 'number') {
      const percentage = Math.round(progressField.value.percent_complete);
      return percentage;
    }
  }
  
  // Fallback: si recibimos solo el status o no hay progreso en custom_fields
  const status = typeof ticket === 'string' ? ticket : ticket?.status;
  const progressMap = {
    'open': 10,
    'abierto': 10,
    'pending': 5,
    'in_progress': 50,
    'en_progreso': 50,
    'resolved': 100,
    'resuelto': 100,
    'closed': 100,
    'cerrado': 100
  };
  
  const fallbackPercentage = progressMap[status?.toLowerCase()] || 0;
  return fallbackPercentage;
}

function getProgressBarClass(status) {
  const progressClasses = {
    'open': 'bg-gradient-to-r from-red-500 to-red-600',
    'abierto': 'bg-gradient-to-r from-red-500 to-red-600',
    'pending': 'bg-gradient-to-r from-gray-500 to-gray-600',
    'in_progress': 'bg-gradient-to-r from-yellow-500 to-yellow-600',
    'en_progreso': 'bg-gradient-to-r from-yellow-500 to-yellow-600',
    'resolved': 'bg-gradient-to-r from-green-500 to-green-600',
    'resuelto': 'bg-gradient-to-r from-green-500 to-green-600',
    'closed': 'bg-gradient-to-r from-gray-500 to-gray-600',
    'cerrado': 'bg-gradient-to-r from-gray-500 to-gray-600'
  };
  return progressClasses[status?.toLowerCase()] || 'bg-gradient-to-r from-gray-400 to-gray-500';
}

// Función para obtener el color del círculo de progreso
function getProgressColor(status) {
  const progressColors = {
    'open': '#ef4444', // red-500
    'abierto': '#ef4444',
    'pending': '#6b7280', // gray-500
    'pendiente': '#6b7280',
    'pendientes': '#6b7280',
    'in_progress': '#f59e0b', // yellow-500
    'en_progreso': '#f59e0b',
    'in progress': '#f59e0b',
    'evaluacion': '#f59e0b',
    'evaluación': '#f59e0b',
    'pendiente_dev': '#f59e0b',
    'pendiente dev': '#f59e0b',
    'en_desarrollo': '#f59e0b',
    'en desarrollo': '#f59e0b',
    'development': '#f59e0b',
    'testeo_local': '#f59e0b',
    'testeo local': '#f59e0b',
    'pendiente_servidor_dev': '#f59e0b',
    'pendiente servidor dev': '#f59e0b',
    'testeo_servidor_dev': '#f59e0b',
    'testeo servidor dev': '#f59e0b',
    'pendiente_a_qa': '#f59e0b',
    'pendiente a qa': '#f59e0b',
    'testeo_servidor_qa': '#f59e0b',
    'testeo servidor qa': '#f59e0b',
    'bugs_encontrados': '#f97316', // orange-500
    'bugs encontrados': '#f97316',
    'resolved': '#10b981', // green-500
    'resuelto': '#10b981',
    'bugs_corregidos': '#10b981',
    'bugs corregidos': '#10b981',
    'listo': '#10b981',
    'closed': '#6b7280', // gray-500
    'cerrado': '#6b7280'
  };
  return progressColors[status?.toLowerCase()] || '#9ca3af'; // gray-400
}

// Función para obtener el color de los indicadores de prioridad
function getPriorityColor(priority) {
  const priorityColors = {
    'high': '#ef4444', // red-500 - Prioridad alta
    'alta': '#ef4444',
    'urgent': '#dc2626', // red-600 - Urgente
    'urgente': '#dc2626',
    'critical': '#b91c1c', // red-700 - Crítico
    'critico': '#b91c1c',
    'crítico': '#b91c1c',
    'medium': '#f59e0b', // yellow-500 - Prioridad media
    'media': '#f59e0b',
    'normal': '#f59e0b',
    'low': '#10b981', // green-500 - Prioridad baja
    'baja': '#10b981',
    'minor': '#059669', // green-600 - Menor
    'menor': '#059669'
  };
  return priorityColors[priority?.toLowerCase()] || '#6b7280'; // gray-500 por defecto
}

// Función para obtener las clases CSS del borde de estado
function getStatusBorderClass(status) {
  const statusBorderClasses = {
    'open': 'border-red-500',
    'abierto': 'border-red-500',
    'pending': 'border-red-500',
    'in_progress': 'border-yellow-500',
    'en_progreso': 'border-yellow-500',
    'in progress': 'border-yellow-500',
    'development': 'border-yellow-500',
    'testing': 'border-yellow-500',
    'qa': 'border-yellow-500',
    'resolved': 'border-green-500',
    'resuelto': 'border-green-500',
    'complete': 'border-green-500',
    'completed': 'border-green-500',
    'done': 'border-green-500',
    'closed': 'border-gray-500',
    'cerrado': 'border-gray-500',
    'finished': 'border-gray-500',
    'listo': 'border-green-500'
  };
  
  return statusBorderClasses[status?.toLowerCase()] || 'border-gray-400';
}

// Función para obtener las clases CSS del borde de estado en hover
function getHoverBorderClass(status) {
  const hoverBorderClasses = {
    'open': 'hover:border-red-500',
    'abierto': 'hover:border-red-500',
    'pending': 'hover:border-red-500',
    'in_progress': 'hover:border-yellow-500',
    'en_progreso': 'hover:border-yellow-500',
    'in progress': 'hover:border-yellow-500',
    'development': 'hover:border-yellow-500',
    'testing': 'hover:border-yellow-500',
    'qa': 'hover:border-yellow-500',
    'resolved': 'hover:border-green-500',
    'resuelto': 'hover:border-green-500',
    'complete': 'hover:border-green-500',
    'completed': 'hover:border-green-500',
    'done': 'hover:border-green-500',
    'closed': 'hover:border-gray-500',
    'cerrado': 'hover:border-gray-500',
    'finished': 'hover:border-gray-500',
    'listo': 'hover:border-green-500'
  };
  
  return hoverBorderClasses[status?.toLowerCase()] || 'hover:border-gray-400';
}

// Watchers
watch(messages, () => {
  scrollToBottom();
}, { deep: true });

// Watch para recalcular estadísticas cuando cambien los tickets
watch(() => ticketsStore?.getAllTickets?.value, (newTickets) => {
  if (!newTickets) {
    return;
  }
}, { deep: true });

// Watch para resetear paginación cuando cambien los filtros
watch([searchQuery, filterStatus, filterPriority], () => {
  resetPagination();
});

// Lifecycle
onMounted(async () => {
  try {
    console.log('🚀 Inicializando DashboardView...');
    
    // Verificar estado de autenticación primero
    // Si no hay usuario, intentar cargar desde localStorage
    if (!authStore.user || !authStore.isAuthenticated) {
      console.log('👤 Usuario no autenticado, intentando auto-login...');
      const loginSuccess = await authStore.autoLogin();
      if (loginSuccess) {
        console.log('✅ Auto-login exitoso');
      } else {
        console.warn('⚠️ Auto-login falló, usuario debe autenticarse manualmente');
        // No bloquear la aplicación si el auto-login falla
        // El usuario puede autenticarse manualmente después
      }
    }
    
    // IMPORTANTE: Cargar tickets silenciosamente al inicializar (sin notificaciones)
    console.log('📊 Cargando tickets iniciales...');
    await loadUserTicketsSilently();
    
    console.log('✅ DashboardView inicializado correctamente');
    
  } catch (error) {
    console.error('❌ Error durante la inicialización del Dashboard:', error);
    
    // No mostrar error al usuario para evitar alarma innecesaria
    // La aplicación puede seguir funcionando aunque haya errores de inicialización
    // Los errores específicos se manejan en loadUserTicketsSilently()
  }
});

onUnmounted(() => {
  // Limpiar estado del chat si es necesario
  isEliasTyping.value = false;
  isLoading.value = false;
  
  // Cleanup window resize listener
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize);
  }
});

// Funciones de filtros avanzados para métricas clickeables
function filterByPriority(priority) {
  
  // Cambiar al panel de tickets
  setActivePanel('tickets');
  
  // Aplicar filtro de prioridad
  filterPriority.value = priority;
  
  // Limpiar otros filtros para mostrar solo por prioridad
  filterStatus.value = '';
  searchQuery.value = '';
  
  // Resetear paginación
  resetPagination();
  
  // Notificar al usuario
  const ticketsCount = getTicketsByPriority(priority);
  const priorityText = formatPriorityText(priority);
  
  setTimeout(() => {
    if (ticketsCount > 0) {
      notificationService.success(
        `Mostrando ${ticketsCount} ticket${ticketsCount > 1 ? 's' : ''} de prioridad ${priorityText}.`
      );
    } else {
      notificationService.info(
        `No se encontraron tickets de prioridad ${priorityText}.`
      );
    }
  }, 500);
}

function filterByStatus(status) {
  
  // Cambiar al panel de tickets
  setActivePanel('tickets');
  
  // Aplicar filtro de estado
  filterStatus.value = status;
  
  // Limpiar otros filtros para mostrar solo por estado
  filterPriority.value = '';
  searchQuery.value = '';
  
  // Resetear paginación
  resetPagination();
  
  // Notificar al usuario
  const ticketsCount = getTicketsByStatus(status);
  const statusText = formatStatusText(status);
  
  setTimeout(() => {
    if (ticketsCount > 0) {
      notificationService.success(
        `Mostrando ${ticketsCount} ticket${ticketsCount > 1 ? 's' : ''} con estado "${statusText}".`
      );
    } else {
      notificationService.info(
        `No se encontraron tickets con estado "${statusText}".`
      );
    }
  }, 500);
}

function filterByOverdue() {
  
  // Cambiar al panel de tickets
  setActivePanel('tickets');
  
  // Para tickets vencidos, necesitamos filtrar manualmente
  // ya que no hay un filtro directo en el UI
  filterStatus.value = '';
  filterPriority.value = '';
  searchQuery.value = '';
  
  // Resetear paginación
  resetPagination();
  
  // La lógica de tickets vencidos se aplicará en el computed filteredTickets
  // usando una función auxiliar
  
  const overdueCount = getOverdueTickets();
  
  setTimeout(() => {
    if (overdueCount > 0) {
      notificationService.warning(
        `Mostrando ${overdueCount} ticket${overdueCount > 1 ? 's' : ''} vencido${overdueCount > 1 ? 's' : ''}. Requieren atención urgente.`
      );
    } else {
      notificationService.success(
        'No hay tickets vencidos. ¡Excelente trabajo!'
      );
    }
  }, 500);
}

function filterByEscalated() {
  
  // Cambiar al panel de tickets
  setActivePanel('tickets');
  
  // Limpiar filtros básicos y usar búsqueda por "escalado"
  filterStatus.value = '';
  filterPriority.value = '';
  searchQuery.value = 'escalado';
  
  // Resetear paginación
  resetPagination();
  
  const escalatedCount = getEscalatedTickets();
  
  setTimeout(() => {
    if (escalatedCount > 0) {
      notificationService.warning(
        `Mostrando ${escalatedCount} ticket${escalatedCount > 1 ? 's' : ''} escalado${escalatedCount > 1 ? 's' : ''}. Casos complejos que requieren atención especializada.`
      );
    } else {
      notificationService.success(
        'No hay tickets escalados actualmente.'
      );
    }
  }, 500);
}

function filterByThisWeek() {
  
  // Cambiar al panel de tickets
  setActivePanel('tickets');
  
  // Limpiar filtros y mostrar todos para que el usuario vea el contexto temporal
  filterStatus.value = '';
  filterPriority.value = '';
  searchQuery.value = '';
  
  // Resetear paginación
  resetPagination();
  
  const thisWeekCount = getThisWeekTickets();
  
  setTimeout(() => {
    if (thisWeekCount > 0) {
      notificationService.info(
        `Mostrando todos los tickets. ${thisWeekCount} ticket${thisWeekCount > 1 ? 's' : ''} creado${thisWeekCount > 1 ? 's' : ''} en la semana del ${currentWeekRange.value}.`
      );
    } else {
      notificationService.info(
        `No se crearon tickets nuevos en la semana del ${currentWeekRange.value}.`
      );
    }
  }, 500);
}

// Funciones auxiliares para contar tickets por criterios específicos
function getTicketsByPriority(priority) {
  const allTickets = ticketsStore?.getAllTickets?.value || [];
  return allTickets.filter(ticket => {
    const ticketPriority = ticket.priority ? ticket.priority.toLowerCase() : '';
    return ticketPriority === priority.toLowerCase();
  }).length;
}

function getTicketsByStatus(status) {
  const allTickets = ticketsStore?.getAllTickets?.value || [];
  return allTickets.filter(ticket => {
    const ticketStatus = ticket.status ? ticket.status.toLowerCase() : '';
    // Normalizar estados para comparación
    const normalizedStatus = status.toLowerCase();
    
    switch (normalizedStatus) {
      case 'abierto':
        return ['open', 'abierto', 'pending'].includes(ticketStatus);
      case 'en_progreso':
        return ['in_progress', 'en_progreso', 'in progress', 'development'].includes(ticketStatus);
      case 'cerrado':
        return ['closed', 'cerrado', 'resolved', 'resuelto', 'complete', 'done', 'listo'].includes(ticketStatus);
      default:
        return ticketStatus === normalizedStatus;
    }
  }).length;
}

function getOverdueTickets() {
  const allTickets = ticketsStore?.getAllTickets?.value || [];
  const now = new Date();
  
  return allTickets.filter(ticket => {
    // Un ticket está vencido si:
    // 1. Tiene fecha de vencimiento (dueDate)
    // 2. La fecha actual es posterior a la fecha de vencimiento
    // 3. El ticket no está cerrado o resuelto
    
    if (!ticket.dueDate) return false;
    
    const dueDate = new Date(ticket.dueDate);
    const isOverdue = now > dueDate;
    
    const status = ticket.status ? ticket.status.toLowerCase() : '';
    const isNotClosed = !['closed', 'cerrado', 'resolved', 'resuelto', 'complete', 'done', 'listo'].includes(status);
    
    return isOverdue && isNotClosed;
  }).length;
}

function getEscalatedTickets() {
  const allTickets = ticketsStore?.getAllTickets?.value || [];
  return allTickets.filter(ticket => {
    // Un ticket está escalado si:
    // 1. Tiene la propiedad escalated=true
    // 2. Tiene tags que indican escalamiento
    // 3. Tiene "escalado" en el título o descripción
    
    if (ticket.escalated === true) return true;
    
    // Verificar tags de escalamiento
    if (ticket.tags && Array.isArray(ticket.tags)) {
      const escalationTags = ['escalado', 'escalation', 'urgent', 'crítico', 'critical'];
      const hasEscalationTag = ticket.tags.some(tag => 
        escalationTags.includes(tag.toLowerCase())
      );
      if (hasEscalationTag) return true;
    }
    
    // Verificar texto en título o descripción
    const title = (ticket.title || '').toLowerCase();
    const description = (ticket.description || '').toLowerCase();
    const escalationKeywords = ['escalado', 'escalación', 'crítico', 'urgente', 'emergency'];
    
    return escalationKeywords.some(keyword => 
      title.includes(keyword) || description.includes(keyword)
    );
  }).length;
}

function getThisWeekTickets() {
  const allTickets = ticketsStore?.getAllTickets?.value || [];
  const startOfWeek = getStartOfWeek();
  const endOfWeek = getEndOfWeek();
  
  return allTickets.filter(ticket => {
    if (!ticket.createdAt) return false;
    
    const createdDate = new Date(ticket.createdAt);
    return createdDate >= startOfWeek && createdDate <= endOfWeek;
  }).length;
}

// Avatar management functions

/**
 * Función para estabilizar el diseño durante la carga de datos desde webhook
 * Previene saltos visuales y mantiene el layout estable
 */
function stabilizeDesignDuringDataLoad() {
  // Marcar que estamos estabilizando datos
  isDataStabilizing.value = true;
  
  // Limpiar timeout anterior si existe
  if (dataStabilizationTimeout.value) {
    clearTimeout(dataStabilizationTimeout.value);
  }
  
  console.log('🔧 Estabilizando diseño durante carga de datos desde webhook...');
}

/**
 * Función para finalizar la estabilización del diseño
 * Se ejecuta cuando los datos han sido procesados completamente
 */
function finishDesignStabilization() {
  // Usar un pequeño delay para asegurar que el DOM se haya actualizado
  dataStabilizationTimeout.value = setTimeout(() => {
    isDataStabilizing.value = false;
    console.log('✅ Estabilización de diseño completada');
  }, 100); // Delay mínimo para estabilizar
}

/**
 * Función que retorna la URL completa del avatar de Elias AI
 * Ya no se necesita porque el avatar se extrae del webhook
 */
function getEliasAvatarUrl() {
  // DEPRECATED: Ahora el avatar se extrae directamente del webhook
  // en TicketService.normalizeTicket() desde assignees[0].avatar
  console.warn('getEliasAvatarUrl() está deprecated, usar ticket.assignedToAvatar del webhook');
  
  // Fallback para compatibilidad
  return null;
}

/**
 * Maneja el evento de error al cargar el avatar de Elias AI
 * Establece el estado de avatarLoaded y activa el fallback visual
 */
function handleAvatarError(event) {
  avatarLoaded.value = false;
  
  // Opcional: Intentar cargar desde una fuente alternativa
  // const img = event.target;
  // if (!img.dataset.retried) {
  //   img.dataset.retried = 'true';
  //   img.src = 'https://ui-avatars.com/api/?name=Elias+AI&background=3b82f6&color=ffffff&size=128';
  // }
}

/**
 * Maneja el evento de carga exitosa del avatar de Elias AI
 * Reactiva el estado de avatarLoaded
 */
function handleAvatarLoad(event) {
  avatarLoaded.value = true;
}

/**
 * Función auxiliar para obtener clases CSS del avatar según el estado
 */
function getAvatarClasses(isLoaded = true) {
  const baseClasses = 'w-full h-full object-cover object-center transition-transform duration-300 group-hover:scale-110';
  const loadedClasses = 'opacity-100';
  const failedClasses = 'opacity-0';
  
  return `${baseClasses} ${isLoaded ? loadedClasses : failedClasses}`;
}

/**
 * Función auxiliar para obtener el contenido del fallback avatar
 */
function getFallbackAvatarContent() {
  return `
    <div class="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-xl flex items-center justify-center relative group transition-all duration-300 hover:scale-105">
      <svg class="w-6 h-6 text-white z-10 relative transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <!-- Animated background -->
      <div class="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
    </div>
  `;
}

// Escalation banner handlers
function handleViewEscalationTicket() {
  // Navegar al ticket escalado específico
  if (escalationTicketId.value) {
    viewTicket(escalationTicketId.value);
  }
}

function handleDismissEscalationBanner() {
  // Para demo, simplemente "cerrar" el escalamiento
  // En un caso real, esto podría marcar como "visto" en el servidor
  console.log('Banner de escalamiento minimizado');
  // Opcionalmente podemos actualizar el estado en el store del chat
  // chatStore.dismissEscalation();
}


// Image error handlers
function handleImageError(event) {
  // Fallback to a gradient avatar if image fails to load
  event.target.style.display = 'none';
  const parent = event.target.parentElement;
  parent.innerHTML = `
    <div class="w-full h-full bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
      <svg class="w-6 h-6 text-white z-10 relative transition-transform duration-300 group-hover:scale-110" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <!-- Animated background -->
      <div class="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      <!-- Status indicator mejorado -->
      <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-3 border-white shadow-lg">
        <div class="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-75"></div>
        <div class="absolute inset-1 rounded-full bg-green-300 animate-pulse"></div>
      </div>
    </div>
  `;
}


function handleWelcomeImageError(event) {
  // Fallback to a gradient avatar if image fails to load
  event.target.style.display = 'none';
  const parent = event.target.parentElement;
  parent.innerHTML = `
    <div class="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
      <svg class="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    </div>
  `;
}

/**
 * Maneja el error de carga de imagen para avatares de agentes humanos
 * Oculta la imagen y activa el fallback de iniciales
 */
function handleHumanAvatarError(event) {
  // Ocultar la imagen que falló
  const img = event.target;
  if (img) {
    img.style.display = 'none';
  }
  
  // El fallback ya está preparado en el template con v-bind de clases
  // El sistema automáticamente mostrará las iniciales cuando la imagen no cargue
}

// Funciones de paginación
function goToPage(page) {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
}

function resetPagination() {
  currentPage.value = 1;
}

// Funciones de paginación específicas del dashboard
function goToDashboardPage(page) {
  if (page >= 1 && page <= dashboardTotalPages.value) {
    dashboardCurrentPage.value = page;
  }
}

function resetDashboardPagination() {
  dashboardCurrentPage.value = 1;
}

function getDashboardVisiblePages() {
  const total = dashboardTotalPages.value;
  const current = dashboardCurrentPage.value;
  const pages = [];
  
  if (total <= 5) {
    // Mostrar todas las páginas si son 5 o menos (más compacto para dashboard)
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // Lógica simplificada para el dashboard
    if (current <= 3) {
      // Cerca del inicio
      pages.push(1, 2, 3, '...', total);
    } else if (current >= total - 2) {
      // Cerca del final
      pages.push(1, '...', total - 2, total - 1, total);
    } else {
      // En el medio
      pages.push(1, '...', current, '...', total);
    }
  }
  
  return pages;
}

function getVisiblePages() {
  const total = totalPages.value;
  const current = currentPage.value;
  const pages = [];
  
  if (total <= 7) {
    // Mostrar todas las páginas si son 7 o menos
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // Lógica compleja para páginas
    if (current <= 4) {
      // Cerca del inicio
      pages.push(1, 2, 3, 4, 5, '...', total);
    } else if (current >= total - 3) {
      // Cerca del final
      pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
    } else {
      // En el medio
      pages.push(1, '...', current - 1, current, current + 1, '...', total);
    }
  }
  
  return pages;
}
</script>
